import React, { useState, useEffect, useRef } from 'react';
import { useBranch } from '../../../layouts/BranchContext';
import BackButton from '../../../ui/BackButton';
import { useSearchParams } from "react-router-dom";

import {
  Button,
  Container,
  FormControl,
  Box,
  Modal,
  MenuItem,
  TextField as MuiTextField,
Select as MuiSelect,
  Typography,
  Tooltip,
  useTheme,
  styled,
  IconButton,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  InputAdornment,
  Avatar,
  CircularProgress,
} from '@mui/material';

import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

import CloseIcon from '@mui/icons-material/Close';
import FormHelperText from '@mui/material/FormHelperText';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PaymentIcon from '@mui/icons-material/Payment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EventIcon from '@mui/icons-material/Event';
import FilterListIcon from '@mui/icons-material/FilterList';


// ============================================================
// WRAPPERS
// ============================================================

const TextField = (props) => <MuiTextField variant="outlined" {...props} />;
const Select = (props) => <MuiSelect variant="outlined" {...props} />;

// ============================================================
// STYLES
// ============================================================

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${
    theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.2)'
      : 'rgba(0,0,0,0.2)'
  } transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const SectionLabel = ({ icon, text }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 2,
      mt: 1,
    }}
  >
    <Box
      sx={{
        width: 3,
        height: 18,
        borderRadius: 4,
        bgcolor: 'primary.main',
      }}
    />

    {icon &&
      React.cloneElement(icon, {
        sx: {
          fontSize: 15,
          color: 'primary.main',
        },
      })}

    <Typography
      sx={{
        fontSize: '0.72rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: 'primary.main',
      }}
    >
      {text}
    </Typography>
  </Box>
);

const StatCard = ({ icon, label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 130,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1.5,
        bgcolor: color + '18',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, {
        sx: {
          color,
          fontSize: 20,
        },
      })}
    </Box>

    <Box>
      <Typography
        sx={{
          fontSize: '0.68rem',
          color: 'text.primary',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: '1.15rem',
          fontWeight: 700,
          color: 'text.primary',
          mt: 0.3,
        }}
      >
        {value}
      </Typography>
    </Box>
  </Paper>
);

const inputSx = {
  '& .MuiInputLabel-root': { color: 'text.primary' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
  '& .MuiInputBase-input': { color: 'text.primary' },
  '& .MuiInputBase-input::placeholder': { color: 'text.disabled', opacity: 1 },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: (theme) => theme.palette.text.disabled,
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: 'text.disabled' },
  '& input[type="date"]': {
    colorScheme: (theme) => (theme.palette.mode === 'dark' ? 'dark' : 'light'),
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.disabled' },

  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    fontSize: '0.875rem',
    height: 38,
    transition: 'box-shadow 0.2s',
    '&:hover fieldset': { borderColor: 'primary.main' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(25,118,210,0.12)' },
  },
  '& .MuiOutlinedInput-input': { py: '8px', px: '10px' },
};

const selectSx = {
  borderRadius: '6px',
  fontSize: '0.875rem',
  height: 38,
  color: 'text.primary',
  transition: 'box-shadow 0.2s',
  '& .MuiSelect-icon': { color: 'text.primary' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.disabled' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
  '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(25,118,210,0.12)' },
  '& .MuiSelect-select': { py: '8px', px: '10px' },
};

const FormRow = ({
  label,
  required,
  children,
  helperText,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      mb: 2,
    }}
  >
    <Box
      sx={{
        width: 180,
        minWidth: 180,
        pt: '9px',
        textAlign: 'right',
        pr: 2,
        fontSize: '0.82rem',
        fontWeight: 500,
        color: 'text.primary',
        lineHeight: 1.3,
      }}
    >
      {label}

      {required && (
        <span
          style={{
            color: '#e53935',
            marginLeft: 2,
          }}
        >
          *
        </span>
      )}
    </Box>

    <Box sx={{ flex: 1 }}>
      {children}

      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            mt: 0.5,
            display: 'block',
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  </Box>
);


// ============================================================
// HELPERS
// ============================================================

const MONTH_FULL = {
  Jan: 'January',
  Feb: 'February',
  Mar: 'March',
  Apr: 'April',
  May: 'May',
  Jun: 'June',
  Jul: 'July',
  Aug: 'August',
  Sep: 'September',
  Oct: 'October',
  Nov: 'November',
  Dec: 'December',
};

const MONTH_SHORT = {
  January: 'Jan',
  February: 'Feb',
  March: 'Mar',
  April: 'Apr',
  May: 'May',
  June: 'Jun',
  July: 'Jul',
  August: 'Aug',
  September: 'Sep',
  October: 'Oct',
  November: 'Nov',
  December: 'Dec',
};

const getDueDate = (monthShort, year) => {
  const monthIndex = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ].indexOf(monthShort);

  if (monthIndex === -1) return '';

  const mm = String(monthIndex + 1).padStart(2, '0');

  return `${year}-${mm}-10`;
};

// Due date for installment #index (0-based): 10th of next month, then each following month
const getInstallmentDueDate = (index) => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + 1 + index, 10);
  const mm = String(d.getMonth() + 1).padStart(2, '0');

  return `${d.getFullYear()}-${mm}-10`;
};

const currentYear = new Date().getFullYear();

const generateYearOptions = () => {
  const years = [];

  for (
    let y = 2020;
    y <= currentYear + 2;
    y++
  ) {
    years.push(y);
  }

  return years;
};

const yearOptions = generateYearOptions();

const normalizeAdmissionCourses = (admission) => {
  const rawCourses =
    admission?.courses ||
    admission?.admission_courses ||
    admission?.course_details ||
    admission?.enrolled_courses ||
    [];

  if (!Array.isArray(rawCourses)) return [];

  return rawCourses.map((item, index) => {
    const nestedCourse =
      item?.course_details ||
      item?.course_data ||
      (item?.course && typeof item.course === 'object' ? item.course : null) ||
      null;

    const courseId =
      item?.course_id ??
      nestedCourse?.course_id ??
      nestedCourse?.id ??
      (typeof item?.course === 'number' || typeof item?.course === 'string'
        ? item.course
        : undefined) ??
      item?.id ??
      `course-${index}`;

    const courseName =
      item?.course_name ||
      item?.name ||
      item?.title ||
      nestedCourse?.course_name ||
      nestedCourse?.name ||
      nestedCourse?.title ||
      (typeof item?.course === 'string' && Number.isNaN(Number(item.course))
        ? item.course
        : `Course ${index + 1}`);

    const fee =
      item?.total_course_fee ??
      item?.course_fee ??
      item?.fee ??
      nestedCourse?.total_course_fee ??
      nestedCourse?.course_fee ??
      nestedCourse?.fee ??
      0;

    return {
      ...item,
      course_id: courseId,
      course_name: courseName,
      course_code: item?.course_code || nestedCourse?.course_code || nestedCourse?.code || '',
      total_course_fee: Number(fee) || 0,
      batches: item?.batches || item?.batch_details || [],
    };
  });
};

const resolveBatches = (candidate) => {
  const courses = candidate?.courses || [];

  const allBatches = courses.flatMap(
    (course) => course.batches || []
  );

  return allBatches.map((b) => ({
    batch_name:
      b.batch_name ||
      b.name ||
      '—',

    batch_code:
      b.batch_code ||
      b.code ||
      '',
  }));
};


// ============================================================
// COMPONENT
// ============================================================

const FeeGeneration = () => {

  const clientData = (() => {
    try {
      return JSON.parse(
        localStorage.getItem('client')
      ) || {};
    } catch {
      return {};
    }
  })();

  const { selectedBranchId, branchList } = useBranch();
const selectedBranch = selectedBranchId ? String(selectedBranchId) : '';
const hasAutoSelected = useRef(false);
  const [ledger, setLedger] = useState('');
  const [bankName, setBankName] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [referenceDate, setReferenceDate] =
    useState('');

  const theme = useTheme();
  const isDarkMode =
    theme.palette.mode === 'dark';

  const [showSuccessModal, setShowSuccessModal] =
    useState(false);

  const [successAction, setSuccessAction] =
    useState('Saved');

  const [showUserGuideModal, setShowUserGuideModal] =
    useState(false);

  const [openSnackbar, setOpenSnackbar] =
    useState(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState('');

  const [snackbarSeverity, setSnackbarSeverity] =
    useState('success');

  const [generatedDate, setGeneratedDate] =
    useState(
      () =>
        new Date()
          .toISOString()
          .split('T')[0]
    );

  const [feeType, setFeeType] =
    useState('Installment');

  const [courseFee, setCourseFee] =
    useState('');

  const [extraAmount, setExtraAmount] =
    useState('');

  const [discount, setDiscount] =
    useState('');

  const [billingBeforeGST, setBillingBeforeGST] =
    useState('');

  const [gstPercentage, setGstPercentage] =
    useState('');

  const [totalFee, setTotalFee] =
    useState('');

  const [paymentMode, setPaymentMode] =
    useState('');

  const [advanceAmount, setAdvanceAmount] =
    useState('');

  const [installmentCount, setInstallmentCount] =
    useState('1');

  const [isSaving, setIsSaving] =
    useState(false);

  const [candidates, setCandidates] =
    useState([]);

  const [selectedCandidate, setSelectedCandidate] =
    useState('');

  const [loadingCandidates, setLoadingCandidates] =
    useState(false);

  const [candidateError, setCandidateError] =
    useState(null);

  const [remarks, setRemarks] =
    useState('');

  const [kitCharges, setKitCharges] =
    useState('');

  const [paymentMethods, setPaymentMethods] =
    useState([]);

  const [
    loadingPaymentMethods,
    setLoadingPaymentMethods,
  ] = useState(true);

  const [
    paymentMethodsError,
    setPaymentMethodsError,
  ] = useState(null);

  const [
    showCandidateModal,
    setShowCandidateModal,
  ] = useState(false);

  const [
    candidateSearchTerm,
    setCandidateSearchTerm,
  ] = useState('');

  const [
    displayCandidateName,
    setDisplayCandidateName,
  ] = useState('');

  const [
    selectedCandidateDetails,
    setSelectedCandidateDetails,
  ] = useState(null);

  // Supports selecting one or multiple courses for the same admission.
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [courseSelectOpen, setCourseSelectOpen] = useState(false);

  const [isEditing, setIsEditing] =
    useState(false);

  const [editingFeeId, setEditingFeeId] =
    useState(null);

  const [filterFromYear, setFilterFromYear] =
    useState(currentYear);

  const [filterToYear, setFilterToYear] =
    useState(currentYear + 1);

  const [isFilterApplied, setIsFilterApplied] =
    useState(false);

  const [selectedMonths, setSelectedMonths] =
    useState([]);

  const [monthlyTotalAmount, setMonthlyTotalAmount] =
    useState('');

  const [installmentRows, setInstallmentRows] =
    useState([]);

  const [feeRecords, setFeeRecords] =
    useState([]);

  const [loadingRecords, setLoadingRecords] =
    useState(false);

  const [page, setPage] =
    useState(0);

  const [rowsPerPage, setRowsPerPage] =
    useState(10);

  const [selectedRecords, setSelectedRecords] =
    useState([]);

  const [deleteDialogOpen, setDeleteDialogOpen] =
    useState(false);

  const [selectedAdmission, setSelectedAdmission] =
    useState();

  const [searchParams] =
    useSearchParams();

  const admissionId =
    searchParams.get('admission_id');

  const token =
    localStorage.getItem('accessToken');

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL;


  // ============================================================
  // DATA
  // ============================================================

  const getAvailableMonths = () => {
    const yr = 2026;

    return [
      { month: 'Mar', year: yr },
      { month: 'Apr', year: yr },
      { month: 'May', year: yr },
      { month: 'Jun', year: yr },
      { month: 'Jul', year: yr },
      { month: 'Aug', year: yr },
      { month: 'Sep', year: yr },
      { month: 'Oct', year: yr },
      { month: 'Nov', year: yr },
      { month: 'Dec', year: yr },
      { month: 'Jan', year: yr + 1 },
      { month: 'Feb', year: yr + 1 },
      { month: 'Mar', year: yr + 1 },
      { month: 'Apr', year: yr + 1 },
    ];
  };

  const availableMonths =
    getAvailableMonths();

  const feeTypeData = [
    {
      id: 1,
      name: 'Installment',
    },
    {
      id: 2,
      name: 'Monthly',
    },
  ];

  const gstData = [
    {
      id: 1,
      name: '0%',
      value: 0,
    },
    {
      id: 2,
      name: '5%',
      value: 5,
    },
    {
      id: 3,
      name: '12%',
      value: 12,
    },
    {
      id: 4,
      name: '18%',
      value: 18,
    },
  ];

  const calculateTotalCourseFee = (
    courses
  ) => {
    if (
      !courses ||
      courses.length === 0
    ) {
      return 0;
    }

    return courses.reduce(
      (sum, course) =>
        sum +
        (parseFloat(
          course.total_course_fee
        ) || 0),
      0
    );
  };


  // ============================================================
  // SNACKBAR
  // ============================================================

  const showSnack = (
    msg,
    sev = 'success'
  ) => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setOpenSnackbar(true);
  };


  // ============================================================
  // ORGANIZATION
  // ============================================================

  const getOrganizationId = () => {
    let sessionUser = {};

    try {
      sessionUser =
        JSON.parse(
          sessionStorage.getItem('user')
        ) || {};
    } catch (e) {}

    const isSuperAdmin =
      sessionUser?.is_admin === true &&
      !sessionUser?.branch_id;

    const selectedBranchObj =
      branchList?.find(
        (b) =>
          String(b.id) ===
          String(selectedBranch)
      );

    return isSuperAdmin
      ? selectedBranchObj?.organization
      : sessionUser?.organization_id ||
          clientData?.id;
  };


  // ============================================================
  // PAYMENT HELPERS
  // ============================================================

  const getModeName = (
    modeId
  ) => {
    const method =
      paymentMethods.find(
        (m) =>
          String(m.id) ===
          String(modeId)
      );

    return (
      method?.name ||
      method?.payment_method ||
      method?.method_name ||
      ''
    ).toLowerCase();
  };

  const isBank = (
    modeId
  ) =>
    getModeName(modeId).includes(
      'bank'
    );

  const isUpi = (
    modeId
  ) =>
    getModeName(modeId).includes(
      'upi'
    );

  const resolvePaymentModeId = (
    value
  ) => {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return '';
    }

    const byId =
      paymentMethods.find(
        (m) =>
          String(m.id) ===
          String(value)
      );

    if (byId) {
      return String(byId.id);
    }

    const normalized =
      String(value)
        .trim()
        .toLowerCase();

    const byName =
      paymentMethods.find(
        (m) =>
          String(
            m.name ||
              m.payment_method ||
              m.method_name ||
              ''
          )
            .trim()
            .toLowerCase() ===
          normalized
      );

    return byName
      ? String(byName.id)
      : '';
  };


  // ============================================================
  // STATS
  // ============================================================

  const totalGenerated =
    feeRecords.length;

  // IMPORTANT:
  // API returns `amount`, not `total_fee`
  const totalFeeAmount =
    feeRecords.reduce(
      (sum, record) =>
        sum +
        Number(
          record.amount ??
            record.total_fee ??
            0
        ),
      0
    );

  const todayStr =
    new Date()
      .toISOString()
      .split('T')[0];

  const todayCount =
    feeRecords.filter(
      (record) =>
        (
          record.generated_date ||
          ''
        ).startsWith(todayStr)
    ).length;


  // ============================================================
  // FETCH CANDIDATES
  // ============================================================

  const fetchCandidates = async (
    fromYear,
    toYear,
    applyFilter
  ) => {
    try {
      setLoadingCandidates(true);
      setCandidateError(null);

      const params = new URLSearchParams();
      const admissionsParams = new URLSearchParams();
      const org = getOrganizationId();

      if (org) params.append('organization', org);
      if (selectedBranch) params.append('branch', selectedBranch);
      if (org) admissionsParams.append('organization_id', org);
      if (selectedBranch) admissionsParams.append('branch_id', selectedBranch);
      if (applyFilter && fromYear && toYear) {
        params.append('from_year', fromYear);
        params.append('to_year', toYear);
        admissionsParams.append('from_year', fromYear);
        admissionsParams.append('to_year', toYear);
      }

      const requestOptions = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };

      // Refresh fee records before filtering courses. The no-fee-generation
      // endpoint removes a candidate after their FIRST fee, even when the
      // admission still has another course waiting for fee generation.
      let latestFeeRecords = feeRecords;
      try {
        const feeResponse = await fetch(
          `${API_BASE_URL}/api/fee_details/fee-generation/list/?${params.toString()}`,
          requestOptions
        );
        if (feeResponse.ok) {
          const feeData = await feeResponse.json();
          latestFeeRecords = Array.isArray(feeData)
            ? feeData
            : feeData.results || feeData.data || [];
          setFeeRecords(latestFeeRecords);
        }
      } catch (feeError) {
        console.warn('Could not refresh fee records while loading candidates.', feeError);
      }

      const admissionsUrl = `${API_BASE_URL}/api/admission/admissions/?${admissionsParams.toString()}`;
      const noFeeUrl = `${API_BASE_URL}/api/fee_details/admissions/no-fee-generation/?${params.toString()}`;

      let list = [];
      let admissionsResponse = await fetch(admissionsUrl, requestOptions);

      // Prefer the full admissions list so a candidate remains selectable
      // when another course still needs a fee record.
      if (admissionsResponse.ok) {
        const admissionsData = await admissionsResponse.json();
        list = Array.isArray(admissionsData)
          ? admissionsData
          : admissionsData.results || admissionsData.data || admissionsData.admissions || [];
      }

      // Keep the existing endpoint as a compatibility fallback.
      if (!list.length) {
        const response = await fetch(noFeeUrl, requestOptions);
        if (!response.ok) throw new Error(`Failed: ${response.status}`);
        const data = await response.json();
        list = Array.isArray(data) ? data : data.results || data.data || [];
      }

      const getAdmissionKey = (candidate) => String(
        candidate?.admission_code || candidate?.admission_no || candidate?.admission_number || ''
      ).trim().toLowerCase();

      const courseAlreadyHasFee = (candidate, course) => {
        const candidateAdmissionKey = getAdmissionKey(candidate);
        const courseId = String(course?.course_id ?? course?.course ?? course?.id ?? '');
        const courseCode = String(course?.course_code || '').trim().toLowerCase();
        const courseName = String(course?.course_name || course?.name || '').trim().toLowerCase();

        return latestFeeRecords.some((record) => {
          const recordAdmissionKey = String(
            record?.admission_no || record?.admission_code || record?.admission_details?.admission_code || ''
          ).trim().toLowerCase();
          const sameAdmission = candidateAdmissionKey && recordAdmissionKey
            ? candidateAdmissionKey === recordAdmissionKey
            : String(record?.admission_id ?? record?.admission ?? '') === String(candidate?.id ?? '');
          if (!sameAdmission) return false;

          const recordCourseId = String(
            record?.course_id ?? record?.course ?? record?.course_details?.course_id ?? record?.course_details?.id ?? ''
          );
          if (courseId && recordCourseId) return courseId === recordCourseId;

          const recordCourseCode = String(record?.course_code || record?.course_details?.course_code || '').trim().toLowerCase();
          if (courseCode && recordCourseCode) return courseCode === recordCourseCode;

          const recordCourseName = String(record?.course_name || record?.course_details?.course_name || '').trim().toLowerCase();
          return Boolean(courseName && recordCourseName && courseName === recordCourseName);
        });
      };

      const mapped = list
        .map((candidate) => {
          const normalizedCourses = normalizeAdmissionCourses(candidate);
          const remainingCourses = normalizedCourses.filter(
            (course) => !courseAlreadyHasFee(candidate, course)
          );
          return {
            ...candidate,
            courses: remainingCourses,
            total_course_fee: calculateTotalCourseFee(remainingCourses),
          };
        })
        .filter((candidate) => candidate.courses.length > 0);

      setCandidates(mapped);

      // URL admission auto selection.
      if (admissionId && !hasAutoSelected.current) {
        const matched = mapped.find(
          (candidate) => Number(candidate.id) === Number(admissionId)
        );

        if (matched && !isEditing) {
          hasAutoSelected.current = true;
          setSelectedCandidate(matched.id);
          setDisplayCandidateName(matched.candidate_name || `Candidate #${matched.id}`);
          setSelectedCandidateDetails(matched);

          const firstCourse = matched.courses?.[0];
          const firstCourseId = firstCourse?.course_id ?? firstCourse?.course ?? firstCourse?.id ?? '';
          const allCourseIds = (matched.courses || [])
            .map((course) => String(course.course_id ?? course.course ?? course.id ?? ''))
            .filter(Boolean);
          setSelectedCourseId(String(firstCourseId || ''));
          setSelectedCourseIds(allCourseIds);
          setCourseFee(firstCourse
            ? String(Number(firstCourse.total_course_fee) || 0)
            : '');
        }
      }
    } catch (err) {
      setCandidateError(err.message);
      showSnack(`Error loading candidates: ${err.message}`, 'error');
    } finally {
      setLoadingCandidates(false);
    }
  };

  // ============================================================
  // FEE RECORDS
  // ============================================================

  const fetchFeeRecords =
    async () => {
      try {
        setLoadingRecords(true);

        const params =
          new URLSearchParams();

        const org =
          getOrganizationId();

        if (org) {
          params.append(
            'organization',
            org
          );
        }

        if (selectedBranch) {
          params.append(
            'branch',
            selectedBranch
          );
        }

        const response =
          await fetch(
            `${API_BASE_URL}/api/fee_details/fee-generation/list/?${params.toString()}`,
            {
              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        if (!response.ok) {
          throw new Error(
            `Failed: ${response.status}`
          );
        }

        const data =
          await response.json();

        setFeeRecords(
          Array.isArray(data)
            ? data
            : data.results ||
              data.data ||
              []
        );
      } catch (err) {
        showSnack(
          `Error loading fee records: ${err.message}`,
          'error'
        );
      } finally {
        setLoadingRecords(false);
      }
    };

  useEffect(() => {
    if (selectedBranch) {
      fetchFeeRecords();
    }
  }, [selectedBranch]);

  useEffect(() => {
    if (selectedBranch) {
      fetchCandidates(
        filterFromYear,
        filterToYear,
        isFilterApplied
      );
    }
  }, [
    token,
    API_BASE_URL,
    selectedBranch,
  ]);


  // ============================================================
  // PAYMENT METHODS
  // ============================================================

  useEffect(() => {
    const fetchPaymentMethods =
      async () => {
        try {
          setLoadingPaymentMethods(
            true
          );

          const response =
            await fetch(
              `${API_BASE_URL}/api/master/payment-methods/`,
              {
                headers: {
                  'Content-Type':
                    'application/json',

                  Authorization:
                    `Bearer ${token}`,
                },
              }
            );

          if (!response.ok) {
            throw new Error(
              `Failed: ${response.status}`
            );
          }

          const data =
            await response.json();

          setPaymentMethods(
            Array.isArray(data)
              ? data
              : data.results ||
                data.data ||
                []
          );
        } catch (err) {
          setPaymentMethodsError(
            err.message
          );
        } finally {
          setLoadingPaymentMethods(
            false
          );
        }
      };

    fetchPaymentMethods();
  }, [
    token,
    API_BASE_URL,
  ]);


  // ============================================================
  // FINANCIAL YEAR FILTER
  // ============================================================

  const handleApplyFilter =
    () => {
      if (
        Number(filterFromYear) >
        Number(filterToYear)
      ) {
        showSnack(
          'From Year cannot be greater than To Year',
          'error'
        );

        return;
      }

      setIsFilterApplied(true);

      fetchCandidates(
        filterFromYear,
        filterToYear,
        true
      );
    };

  const handleClearFilter =
    () => {
      setFilterFromYear(
        currentYear
      );

      setFilterToYear(
        currentYear + 1
      );

      setIsFilterApplied(false);

      fetchCandidates(
        null,
        null,
        false
      );
    };


  // ============================================================
  // INSTALLMENT CALCULATIONS
  // ============================================================

  useEffect(() => {
    if (
      feeType ===
      'Installment'
    ) {
      const val =
        (Number(courseFee) || 0) +
        (Number(kitCharges) || 0) +
        (Number(extraAmount) || 0) -
        (Number(discount) || 0);

      setBillingBeforeGST(
        val >= 0
          ? val.toFixed(2)
          : '0.00'
      );
    }
  }, [
    courseFee,
    kitCharges,
    extraAmount,
    discount,
    feeType,
  ]);

  useEffect(() => {
    if (
      feeType ===
      'Installment'
    ) {
      const billing =
        Number(
          billingBeforeGST
        ) || 0;

      const gstItem =
        gstData.find(
          (g) =>
            Number(g.id) ===
            Number(gstPercentage)
        );

      const gstRate =
        gstItem
          ? gstItem.value / 100
          : 0;

      setTotalFee(
        (
          billing +
          billing * gstRate
        ).toFixed(2)
      );
    }
  }, [
    billingBeforeGST,
    gstPercentage,
    feeType,
  ]);


  // ============================================================
  // INSTALLMENT COUNT CHANGE
  // Prevent negative / zero count
  // ============================================================

  const handleInstallmentCountChange =
    (e) => {
      const value =
        e.target.value;

      if (value === '') {
        setInstallmentCount('');
        return;
      }

      const parsed =
        parseInt(
          value,
          10
        );

      if (
        Number.isNaN(parsed)
      ) {
        setInstallmentCount('1');
        return;
      }

     const safeCount = Math.min(12, Math.max(1, parsed));

      setInstallmentCount(
        String(safeCount)
      );
    };


  // ============================================================
  // INSTALLMENT ROW GENERATION
  // ============================================================

  useEffect(() => {
    if (
      feeType !==
      'Installment'
    ) {
      return;
    }

    const count =
      Math.max(
        1,
        parseInt(
          installmentCount,
          10
        ) || 0
      );

    if (
      count <= 0 ||
      !totalFee ||
      Number(totalFee) <= 0
    ) {
      setInstallmentRows(
        []
      );

      return;
    }

    const total =
      Number(totalFee);

    const base =
      Number(
        (
          total / count
        ).toFixed(2)
      );

    const newRows =
      Array.from(
        {
          length: count,
        },
        (_, index) => {
          const amount =
            index ===
            count - 1
              ? Number(
                  (
                    total -
                    base *
                      (count - 1)
                  ).toFixed(2)
                )
              : base;

          return {
            installment_no:
              index + 1,

            amount:
              amount.toFixed(2),

            due_date: getInstallmentDueDate(index),
          };
        }
      );

    setInstallmentRows(
      (previousRows) =>
        newRows.map(
          (
            newRow,
            index
          ) => ({
            ...newRow,

            due_date:
              previousRows[
                index
              ]?.due_date ||
              newRow.due_date,
          })
        )
    );
  }, [
    installmentCount,
    totalFee,
    feeType,
  ]);


  // ============================================================
  // MONTHLY ROW GENERATION
  // ============================================================

  useEffect(() => {
    if (
      feeType !==
      'Monthly'
    ) {
      return;
    }

    const count =
      selectedMonths.length;

    const total =
      parseFloat(courseFee) ||
      0;

    if (
      count === 0 ||
      total <= 0
    ) {
      setInstallmentRows(
        []
      );

      setMonthlyTotalAmount(
        ''
      );

      return;
    }

    const base =
      parseFloat(
        (
          total / count
        ).toFixed(2)
      );

    const lastAmount =
      parseFloat(
        (
          total -
          base *
            (count - 1)
        ).toFixed(2)
      );

    setMonthlyTotalAmount(
      total.toFixed(2)
    );

    setInstallmentRows(
      selectedMonths.map(
        (
          month,
          index
        ) => ({
          installment_no:
            index + 1,

          amount:
            (
              index ===
              count - 1
                ? lastAmount
                : base
            ).toFixed(2),

          due_date:
            getDueDate(
              month.month,
              month.year
            ),
        })
      )
    );
  }, [
    selectedMonths,
    courseFee,
    feeType,
  ]);


  // ============================================================
  // MONTH TOGGLE
  // ============================================================

  const handleMonthToggle =
    (monthObj) => {
      setSelectedMonths(
        (previous) => {
          const exists =
            previous.find(
              (month) =>
                month.month ===
                  monthObj.month &&
                month.year ===
                  monthObj.year
            );

          if (exists) {
            return previous.filter(
              (month) =>
                !(
                  month.month ===
                    monthObj.month &&
                  month.year ===
                    monthObj.year
                )
            );
          }

          const next = [
            ...previous,
            {
              month:
                monthObj.month,

              year:
                monthObj.year,
            },
          ];

          const order =
            availableMonths.map(
              (item) =>
                `${item.month}-${item.year}`
            );

          next.sort(
            (a, b) =>
              order.indexOf(
                `${a.month}-${a.year}`
              ) -
              order.indexOf(
                `${b.month}-${b.year}`
              )
          );

          return next;
        }
      );
    };


  const updateInstallmentRow =
    (
      index,
      field,
      value
    ) => {
      setInstallmentRows(
        (previous) =>
          previous.map(
            (row, rowIndex) =>
              rowIndex === index
                ? {
                    ...row,
                    [field]:
                      value,
                  }
                : row
          )
      );
    };


  // ============================================================
  // VALIDATION
  // ============================================================

  const validateInstallmentRows =
    () => {
      for (
        const row of installmentRows
      ) {
        if (!row.due_date) {
          return `Please set due date for Installment ${row.installment_no}`;
        }

        if (
          !row.amount ||
          Number(row.amount) <= 0
        ) {
          return `Please enter a valid amount for Installment ${row.installment_no}`;
        }
      }

      if (
        feeType ===
          'Installment' &&
        installmentRows.length > 0
      ) {
        const sumOfRows =
          installmentRows.reduce(
            (sum, row) =>
              sum +
              Number(
                row.amount || 0
              ),
            0
          );

        const total =
          Number(totalFee);

        if (
          Math.abs(
            sumOfRows - total
          ) > 1
        ) {
          return `Installment amounts sum (₹${sumOfRows.toFixed(
            2
          )}) must equal Total Fee (₹${total.toFixed(
            2
          )})`;
        }
      }

      return null;
    };


  // ============================================================
  // SAVE / UPDATE
  // ============================================================

  const handleSave =
    async (e) => {
      e.preventDefault();

    if (
  (!isEditing && !selectedCandidate) ||
  !feeType ||
  !generatedDate
) {
        showSnack(
          'Please fill all required fields: Candidate, Fee Type, and Generated Date',
          'error'
        );

        return;
      }

      if (
        paymentMode &&
        isBank(paymentMode) &&
        (
          !ledger.trim() ||
          !bankName.trim() ||
          !referenceNo.trim() ||
          !referenceDate
        )
      ) {
        showSnack(
          'Ledger, Bank Name, Reference No, and Reference Date are required for Bank payment',
          'error'
        );

        return;
      }

      if (
        paymentMode &&
        isUpi(paymentMode) &&
        (
          !referenceNo.trim() ||
          !referenceDate
        )
      ) {
        showSnack(
          'Reference No and Reference Date are required for UPI payment',
          'error'
        );

        return;
      }

      const rowError =
        validateInstallmentRows();

      if (rowError) {
        showSnack(
          rowError,
          'error'
        );

        return;
      }

      const bankFields =
        paymentMode &&
        isBank(paymentMode)
          ? {
              ledger:
                ledger.trim(),

              bank_name:
                bankName.trim(),

              reference_no:
                referenceNo.trim(),

              reference_date:
                referenceDate,
            }
          : {};

      const upiFields =
        paymentMode &&
        isUpi(paymentMode)
          ? {
              reference_no:
                referenceNo.trim(),

              reference_date:
                referenceDate,
            }
          : {};

          // Send only the course selected in the course-wise fee selector.
const courseId = selectedCourseId || selectedCourseIds[0] || '';

if (!isEditing && selectedCourseIds.length === 0 && !courseId) {
  showSnack('Please select at least one course for fee generation', 'error');
  return;
}

      let payload;

      if (
        feeType ===
        'Monthly'
      ) {
        if (
          !courseFee ||
          Number(courseFee) <= 0
        ) {
          showSnack(
            'Please enter Total Fee amount',
            'error'
          );

          return;
        }

        if (
          selectedMonths.length ===
          0
        ) {
          showSnack(
            'Please select at least one month',
            'error'
          );

          return;
        }

        payload = {
         ...(selectedCandidate ? { admission: Number(selectedCandidate) } : {}),
...(courseId
  ? { course: Number(courseId), course_id: Number(courseId) }
  : {}),

          fee_type:
            'Monthly',

          generated_date:
            generatedDate,

          monthly_fee:
            parseFloat(
              (
                Number(courseFee) /
                selectedMonths.length
              ).toFixed(2)
            ),

          total_fee:
            parseFloat(
              monthlyTotalAmount
            ),

          months:
            selectedMonths.map(
              (month) => ({
                month:
                  MONTH_FULL[
                    month.month
                  ] ||
                  month.month,

                year:
                  month.year,
              })
            ),

          installments:
            installmentRows.map(
              (row) => ({
                installment_no:
                  row.installment_no,

                amount:
                  parseFloat(
                    row.amount
                  ),

                due_date:
                  row.due_date,
              })
            ),

          advance_amount:
            Number(
              advanceAmount
            ) || 0,

          payment_mode:
            paymentMode
              ? Number(
                  paymentMode
                )
              : null,

          remarks:
            remarks.trim() ||
            '',

          ...bankFields,
          ...upiFields,
        };
      } else if (
        feeType ===
        'Installment'
      ) {
        if (
          !courseFee ||
          Number(courseFee) <= 0
        ) {
          showSnack(
            'Please enter Course Fee',
            'error'
          );

          return;
        }

        const safeInstallmentCount =
          Math.max(
            1,
            Number(
              installmentCount
            ) || 0
          );

        if (
          safeInstallmentCount <=
          0
        ) {
          showSnack(
            'Please enter Installment Count',
            'error'
          );

          return;
        }

        if (
          installmentRows.length ===
          0
        ) {
          showSnack(
            'Please generate installment schedule',
            'error'
          );

          return;
        }

        const gstItem =
          gstData.find(
            (gst) =>
              Number(
                gst.id
              ) ===
              Number(
                gstPercentage
              )
          );

        const gstPercentValue =
          gstItem
            ? gstItem.value
            : 0;

        payload = {
         ...(selectedCandidate ? { admission: Number(selectedCandidate) } : {}),
...(courseId
  ? { course: Number(courseId), course_id: Number(courseId) }
  : {}),

          fee_type:
            'Installment',

          generated_date:
            generatedDate,

          course_fee:
            Number(courseFee),

          extra_amount:
            Number(
              extraAmount
            ) || 0,

          discount:
            Number(
              discount
            ) || 0,

          kit_charges:
            Number(
              kitCharges
            ) || 0,

          gst_percentage:
            gstPercentValue,

          advance_amount:
            Number(
              advanceAmount
            ) || 0,

          installment_count:
            safeInstallmentCount,

          payment_mode:
            paymentMode
              ? Number(
                  paymentMode
                )
              : null,

          remarks:
            remarks.trim() ||
            '',

          installments:
            installmentRows.map(
              (row) => ({
                installment_no:
                  row.installment_no,

                amount:
                  Number(
                    row.amount
                  ),

                due_date:
                  row.due_date,
              })
            ),

          ...bankFields,
          ...upiFields,
        };
      } else {
        showSnack(
          'Invalid Fee Type selected',
          'error'
        );

        return;
      }

      const wasEditing =
        isEditing;

      setIsSaving(true);

      try {
        // Multi-course creation uses the bulk endpoint. Each selected course
        // gets its own fee object; installments are scaled to that course's fee.
        if (!wasEditing && selectedCourseIds.length > 1) {
          const selectedCourses = (selectedCandidateDetails?.courses || []).filter((course) =>
            selectedCourseIds.includes(String(course.course_id ?? course.course ?? course.id ?? ''))
          );
          const referenceCourseFee = Number(courseFee) || 0;
          if (!selectedCourses.length || referenceCourseFee <= 0) {
            throw new Error('Please select courses and enter a valid course fee.');
          }

          const bulkFees = selectedCourses.map((course) => {
            const id = Number(course.course_id ?? course.course ?? course.id);
            const courseSpecificFee = Number(course.total_course_fee) || referenceCourseFee;
            const ratio = courseSpecificFee / referenceCourseFee;
            const scaledInstallments = installmentRows.map((row) => ({
              installment_no: Number(row.installment_no),
              amount: Number((Number(row.amount) * ratio).toFixed(2)),
              due_date: row.due_date,
            }));
            if (feeType === 'Monthly') {
              if (!selectedMonths.length) {
                throw new Error('Please select at least one month for monthly fee generation.');
              }
              return {
                course_id: id,
                fee_type: 'Monthly',
                generated_date: generatedDate,
                monthly_fee: Number(((courseSpecificFee / selectedMonths.length)).toFixed(2)),
                total_fee: Number(((Number(monthlyTotalAmount) || referenceCourseFee) * ratio).toFixed(2)),
                months: selectedMonths.map((month) => ({
                  month: MONTH_FULL[month.month] || month.month,
                  year: month.year,
                })),
                installments: scaledInstallments,
                advance_amount: Number(advanceAmount) || 0,
                payment_mode: paymentMode ? Number(paymentMode) : null,
                remarks: remarks.trim() || '',
                ...bankFields,
                ...upiFields,
              };
            }

            return {
              course_id: id,
              fee_type: 'Installment',
              generated_date: generatedDate,
              course_fee: courseSpecificFee,
              gst_percentage: (gstData.find((gst) => Number(gst.id) === Number(gstPercentage))?.value) || 0,
              extra_amount: Number(extraAmount) || 0,
              discount: Number(discount) || 0,
              kit_charges: Number(kitCharges) || 0,
              advance_amount: Number(advanceAmount) || 0,
              installment_count: Math.max(1, Number(installmentCount) || scaledInstallments.length),
              payment_mode: paymentMode ? Number(paymentMode) : null,
              installments: scaledInstallments,
              remarks: remarks.trim() || '',
              ...bankFields,
              ...upiFields,
            };
          });

          const bulkResponse = await fetch(`${API_BASE_URL}/api/fee_details/fee-generation/bulk-insert/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              admission: Number(selectedCandidate),
              fees: bulkFees,
            }),
          });
          const bulkData = await bulkResponse.json().catch(() => ({}));
          if (!bulkResponse.ok) {
            throw new Error(bulkData.error || bulkData.detail || bulkData.message || `Bulk fee generation failed: ${bulkResponse.status}`);
          }

          showSnack(bulkData.message || `Fees generated for ${selectedCourses.length} courses successfully`);
          setSuccessAction('Saved');
          setShowSuccessModal(true);
          handleReset();
          await fetchFeeRecords();
          await fetchCandidates(filterFromYear, filterToYear, isFilterApplied);
          return;
        }

        const url =
          wasEditing
            ? `${API_BASE_URL}/api/fee_details/fee-generation/update/${editingFeeId}/`
            : `${API_BASE_URL}/api/fee_details/fee-generation/insert-update/`;

        const method =
          wasEditing
            ? 'PUT'
            : 'POST';

        const response =
          await fetch(
            url,
            {
              method,

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify(
                  payload
                ),
            }
          );

        if (!response.ok) {
          const errData =
            await response
              .json()
              .catch(
                () => ({})
              );
throw new Error(
  errData.error ||
    errData.detail ||
    errData.message ||
    `Failed: ${response.status}`
);
        }

        showSnack(
          wasEditing
            ? 'Fee record updated successfully'
            : 'Fee record saved successfully'
        );

        setSuccessAction(
          wasEditing
            ? 'Updated'
            : 'Saved'
        );

        setShowSuccessModal(
          true
        );

        const keepAdmissionForNextCourse =
          !wasEditing &&
          (selectedCandidateDetails?.courses?.length || 0) > 1;

        if (keepAdmissionForNextCourse) {
          const savedCourseId = String(selectedCourseId || '');
          setSelectedCandidateDetails((previous) => {
            if (!previous) return previous;
            const remainingCourses = (previous.courses || []).filter((course) =>
              String(course.course_id ?? course.course ?? course.id ?? '') !== savedCourseId
            );
            return {
              ...previous,
              courses: remainingCourses,
              total_course_fee: calculateTotalCourseFee(remainingCourses),
            };
          });
        }

        handleReset({ preserveCandidate: keepAdmissionForNextCourse });

        await fetchFeeRecords();
        await fetchCandidates(
          filterFromYear,
          filterToYear,
          isFilterApplied
        );
      } catch (err) {
        showSnack(
          `${
            wasEditing
              ? 'Update'
              : 'Save'
          } failed: ${
            err.message
          }`,
          'error'
        );
      } finally {
        setIsSaving(false);
      }
    };


  // ============================================================
  // EDIT
  // ============================================================

  const handleEdit =
    (record) => {
      setIsEditing(true);

      setEditingFeeId(
        record.fee_id
      );


      // --------------------------------------------------------
      // Candidate mapping
      // API gives admission_no, NOT admission_id.
      // --------------------------------------------------------

      const matchedCandidate =
        candidates.find(
          (candidate) =>
            String(
              candidate.admission_no ||
                candidate.admission_code ||
                ''
            ) ===
            String(
              record.admission_no ||
                ''
            )
        ) ||
        candidates.find(
          (candidate) =>
            String(
              candidate.candidate_name ||
                ''
            )
              .trim()
              .toLowerCase() ===
            String(
              record.candidate_name ||
                ''
            )
              .trim()
              .toLowerCase()
        );

      if (
        matchedCandidate
      ) {
        setSelectedCandidate(
          matchedCandidate.id
        );

        setSelectedCandidateDetails(
          matchedCandidate
        );
        const recordCourseId = record.course_id ?? record.course ?? record.course_details?.course_id ?? '';
        const fallbackCourse = matchedCandidate.courses?.[0];
        const editCourseId = String(recordCourseId || fallbackCourse?.course_id || fallbackCourse?.course || fallbackCourse?.id || '');
        setSelectedCourseId(editCourseId);
        setSelectedCourseIds(editCourseId ? [editCourseId] : []);
      } else {
        // Do not invent an admission ID.
        setSelectedCandidate(
          record.admission ||
            record.admission_id ||
            ''
        );

        setSelectedCandidateDetails(
          null
        );
        const editCourseId = String(record.course_id ?? record.course ?? '');
        setSelectedCourseId(editCourseId);
        setSelectedCourseIds(editCourseId ? [editCourseId] : []);
      }

      setDisplayCandidateName(
        record.candidate_name ||
          ''
      );


      // --------------------------------------------------------
      // General fields
      // --------------------------------------------------------

      setGeneratedDate(
        record.generated_date
          ? String(
              record.generated_date
            ).slice(0, 10)
          : ''
      );

      setFeeType(
        record.fee_type ||
          'Installment'
      );


      // --------------------------------------------------------
      // Payment mode
      // API gives "CASH", "BANK", "UPI", etc.
      // Frontend Select expects payment method ID.
      // --------------------------------------------------------

      const resolvedPaymentMode =
        resolvePaymentModeId(
          record.payment_mode
        );

      setPaymentMode(
        resolvedPaymentMode
      );


      setAdvanceAmount(
        String(
          record.advance_amount ??
            record.advance_paid ??
            ''
        )
      );

      setRemarks(
        record.remarks ||
          ''
      );

      setLedger(
        record.ledger ||
          ''
      );

      setBankName(
        record.bank_name ||
          ''
      );

      setReferenceNo(
        record.reference_no ||
          ''
      );

      setReferenceDate(
        record.reference_date
          ? String(
              record.reference_date
            ).slice(0, 10)
          : ''
      );


      // --------------------------------------------------------
      // Common amount fields
      // --------------------------------------------------------

      setExtraAmount(
        String(
          record.extra_amount ??
            ''
        )
      );

      setDiscount(
        String(
          record.discount ??
            ''
        )
      );

      setKitCharges(
        String(
          record.kit_charges ??
            ''
        )
      );


      // --------------------------------------------------------
      // GST
      // --------------------------------------------------------

      const gstItem =
        gstData.find(
          (gst) =>
            Number(
              gst.value
            ) ===
            Number(
              record.gst_percentage ??
                0
            )
        );

      setGstPercentage(
        gstItem
          ? gstItem.id
          : ''
      );


      // --------------------------------------------------------
      // MONTHLY
      // Actual API:
      //
      // monthly_details: {
      //   months: [
      //      { month: "August", year: 2026, due_date: null }
      //   ],
      //   months_count: 4,
      //   monthly_fee: 13750
      // }
      //
      // total amount is API `amount`
      // --------------------------------------------------------

      if (
        record.fee_type ===
        'Monthly'
      ) {
        const monthlyDetails =
          record.monthly_details ||
          {};

        const apiMonths =
          monthlyDetails.months ||
          [];

        const normalizedMonths =
          apiMonths.map(
            (item) => ({
              month:
                MONTH_SHORT[
                  item.month
                ] ||
                item.month,

              year:
                Number(
                  item.year
                ),
            })
          );

        const total =
          Number(
            record.amount ??
              record.total_fee ??
              record.course_fee ??
              0
          );

        setCourseFee(
          total > 0
            ? String(total)
            : ''
        );

        setMonthlyTotalAmount(
          total > 0
            ? total.toFixed(2)
            : ''
        );

        setSelectedMonths(
          normalizedMonths
        );

        setInstallmentCount(
          String(
            monthlyDetails.months_count ??
              normalizedMonths.length ??
              ''
          )
        );

        setInstallmentRows(
          normalizedMonths.map(
            (month, index) => {
              const existing =
                (
                  record.installments ||
                  []
                ).find(
                  (item) =>
                    Number(
                      item.installment_no
                    ) ===
                    index + 1
                );

              return {
                installment_no:
                  index + 1,

                amount:
                  existing?.amount !==
                  undefined
                    ? String(
                        existing.amount
                      )
                    : '',

                due_date:
                  existing?.due_date ||
                  getDueDate(
                    month.month,
                    month.year
                  ),
              };
            }
          )
        );
      }


      // --------------------------------------------------------
      // INSTALLMENT
      // --------------------------------------------------------

      else {
        setCourseFee(
          String(
            record.course_fee ??
              ''
          )
        );

        const existingInstallments =
          record.installments ||
          [];

        const apiCount =
          existingInstallments.length >
          0
            ? existingInstallments.length
            : Number(
                record.installment_count ||
                  0
              );

        setInstallmentCount(
          apiCount > 0
            ? String(apiCount)
            : ''
        );

        setSelectedMonths(
          []
        );

        setMonthlyTotalAmount(
          ''
        );

        setInstallmentRows(
          existingInstallments.map(
            (item) => ({
              installment_no:
                item.installment_no,

              amount:
                String(
                  item.amount ??
                    ''
                ),

              due_date:
                item.due_date ||
                '',
            })
          )
        );
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    };


  // ============================================================
  // DELETE
  // ============================================================

  const handleDelete =
    async () => {
      if (
        selectedRecords.length ===
        0
      ) {
        showSnack(
          'Please select records to delete',
          'warning'
        );

        return;
      }

      try {
        const response =
          await fetch(
            `${API_BASE_URL}/api/fee_details/fee-generation/delete-bulk/`,
            {
              method: 'DELETE',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`,
              },

              body:
                JSON.stringify({
                  fee_ids:
                    selectedRecords,
                }),
            }
          );

        if (!response.ok) {
          throw new Error(
            `Failed: ${response.status}`
          );
        }

        const data =
          await response.json();

        const results =
          data.results || [];

        const failed =
          results.filter(
            (item) =>
              !item.deleted
          );

          const succeeded = results.filter((item) => item.deleted);

// Reset the form if the record being edited was deleted
const deletedIds = succeeded.map((item) => item.fee_id);

if (
  isEditing &&
  deletedIds.some((id) => String(id) === String(editingFeeId))
) {
  handleReset();
}

        if (
          failed.length > 0 &&
          succeeded.length === 0
        ) {
          showSnack(
            `Delete failed: ${failed
              .map((item) =>
                item.error ===
                'has payments'
                  ? 'Has existing payments'
                  : item.error
              )
              .join('; ')}`,
            'error'
          );
        } else if (
          failed.length > 0
        ) {
          showSnack(
            `${succeeded.length} deleted, ${failed.length} skipped (has payments)`,
            'warning'
          );
        } else {
          showSnack(
            `${succeeded.length} record(s) deleted successfully`
          );
        }

       setSelectedRecords([]);
setPage(0);

await Promise.all([
  fetchFeeRecords(),
  fetchCandidates(
    filterFromYear,
    filterToYear,
    isFilterApplied
  ),
]);
      } catch (err) {
        showSnack(
          `Delete failed: ${err.message}`,
          'error'
        );
      } finally {
        setDeleteDialogOpen(
          false
        );
      }
    };


  // ============================================================
  // SELECTION
  // ============================================================

  const handleSelectAllClick = (e) => {
  if (e.target.checked) {
    setSelectedRecords(feeRecords.map((record) => record.fee_id));
    return;
  }

  setSelectedRecords([]);
};

  const handleSelectRecordClick =
    (id) => {
      const index =
        selectedRecords.indexOf(
          id
        );

      if (index === -1) {
        setSelectedRecords(
          (previous) => [
            ...previous,
            id,
          ]
        );
      } else {
        setSelectedRecords(
          (previous) =>
            previous.filter(
              (item) =>
                item !== id
            )
        );
      }
    };


  // ============================================================
  // CANDIDATE SELECT
  // ============================================================

  const handleCandidateSelect =
    async (candidate) => {
      let admissionDetails = candidate;

      // The fee-generation candidate list may omit course names or return
      // only a partial course list. Try the admission detail endpoint first.
      try {
        const detailResponse = await fetch(
          `${API_BASE_URL}/api/admission/admissions/${candidate.id}/`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (detailResponse.ok) {
          const detailData = await detailResponse.json();
          admissionDetails = { ...candidate, ...detailData };
        }
      } catch (detailError) {
        console.warn('Could not load admission course details; using candidate list data.', detailError);
      }

      const allNormalizedCourses = normalizeAdmissionCourses(admissionDetails);
      const candidateAdmissionKey = String(
        admissionDetails?.admission_code || admissionDetails?.admission_no || admissionDetails?.admission_number || candidate?.admission_code || candidate?.admission_no || ''
      ).trim().toLowerCase();
      const normalizedCourses = allNormalizedCourses.filter((course) => {
        const courseId = String(course?.course_id ?? course?.course ?? course?.id ?? '');
        const courseCode = String(course?.course_code || '').trim().toLowerCase();
        const courseName = String(course?.course_name || course?.name || '').trim().toLowerCase();
        return !feeRecords.some((record) => {
          const recordAdmissionKey = String(
            record?.admission_no || record?.admission_code || record?.admission_details?.admission_code || ''
          ).trim().toLowerCase();
          const sameAdmission = candidateAdmissionKey && recordAdmissionKey
            ? candidateAdmissionKey === recordAdmissionKey
            : String(record?.admission_id ?? record?.admission ?? '') === String(candidate?.id ?? '');
          if (!sameAdmission) return false;
          const recordCourseId = String(
            record?.course_id ?? record?.course ?? record?.course_details?.course_id ?? record?.course_details?.id ?? ''
          );
          if (courseId && recordCourseId) return courseId === recordCourseId;
          const recordCourseCode = String(record?.course_code || record?.course_details?.course_code || '').trim().toLowerCase();
          if (courseCode && recordCourseCode) return courseCode === recordCourseCode;
          const recordCourseName = String(record?.course_name || record?.course_details?.course_name || '').trim().toLowerCase();
          return Boolean(courseName && recordCourseName && courseName === recordCourseName);
        });
      });
      const selectedCandidateData = {
        ...admissionDetails,
        courses: normalizedCourses,
        total_course_fee: calculateTotalCourseFee(normalizedCourses),
      };

      setSelectedCandidate(candidate.id);
      setDisplayCandidateName(
        selectedCandidateData.candidate_name || `Candidate #${candidate.id}`
      );
      setSelectedCandidateDetails(selectedCandidateData);

      const firstCourse = normalizedCourses[0];
      const firstCourseId = firstCourse?.course_id ?? firstCourse?.course ?? firstCourse?.id ?? '';
      const allCourseIds = normalizedCourses
        .map((course) => String(course.course_id ?? course.course ?? course.id ?? ''))
        .filter(Boolean);
      // Select all courses by default for fee generation.
      setSelectedCourseId(String(firstCourseId || ''));
      setSelectedCourseIds(allCourseIds);
      setCourseFee(firstCourse
        ? String(Number(firstCourse.total_course_fee) || 0)
        : '');

      if (normalizedCourses.length === 0) {
        showSnack('No course details were returned for this admission. Please check the admission API response.', 'warning');
      }

      setCandidateSearchTerm('');
      setShowCandidateModal(false);
    };


  // ============================================================
  // RESET
  // ============================================================

  const handleReset =
    ({ preserveCandidate = false } = {}) => {
      setLedger('');
      setBankName('');
      setReferenceNo('');
      setReferenceDate('');

      setIsEditing(false);
      setEditingFeeId(null);

      if (!preserveCandidate) {
        setSelectedCandidate('');
        setDisplayCandidateName('');
        setSelectedCandidateDetails(null);
        setSelectedCourseId('');
        setSelectedCourseIds([]);
      } else {
        // Clear the course selection so the next course must be chosen explicitly.
        setSelectedCourseId('');
        setSelectedCourseIds([]);
      }

      setGeneratedDate(
        new Date()
          .toISOString()
          .split('T')[0]
      );

      setFeeType(
        'Installment'
      );

      setCourseFee('');
      setExtraAmount('');
      setDiscount('');
      setBillingBeforeGST('');
      setGstPercentage('');
      setTotalFee('');

      setPaymentMode('');
      setAdvanceAmount('');
      setInstallmentCount('1');

      setRemarks('');
      setKitCharges('');

      setSelectedMonths([]);
      setMonthlyTotalAmount('');

      setInstallmentRows([]);
    };


    useEffect(() => {
  handleReset();
}, [selectedBranch]);


  // ============================================================
  // FORM CHECK
  // ============================================================

  const isFormFilled =
    () => {
      if (
        !generatedDate ||
        !feeType
      ) {
        return false;
      }

      if (
        !isEditing &&
        !selectedCandidate
      ) {
        return false;
      }

      if (
        feeType ===
        'Monthly'
      ) {
        return !!(
          courseFee &&
          Number(courseFee) >
            0 &&
          selectedMonths.length >
            0
        );
      }

      if (
        feeType ===
        'Installment'
      ) {
        return !!(
          courseFee &&
          Number(courseFee) >
            0 &&
          installmentCount &&
          Number(
            installmentCount
          ) > 0
        );
      }

      return false;
    };


  // ============================================================
  // MODAL
  // ============================================================

  const modalBox =
    (width) => ({
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform:
        'translate(-50%, -50%)',

      width: {
        xs: '95%',
        sm: width || 420,
      },

      bgcolor:
        'background.paper',

      borderRadius: 2,

      boxShadow: 24,

      overflow: 'hidden',
    });

  const modalHeader = {
    background:
      `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,

    px: 3,
    py: 2,

    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'space-between',
  };


  const feeTypeColor =
    (type) => {
      if (
        type ===
        'Installment'
      ) {
        return {
          color: 'info',
        };
      }

      if (
        type ===
        'Monthly'
      ) {
        return {
          color: 'secondary',
        };
      }

      return {
        color: 'default',
      };
    };


  // ============================================================
  // JSX
  // ============================================================

  return (
    <Container
      sx={{
        maxWidth: '100%',
        boxSizing: 'border-box',
        py: 3,
        px: {
          xs: 1,
          sm: 2,
        },
      }}
    >
      <BackButton />

      {/* ======================================================
          STATS
      ====================================================== */}

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <StatCard
          icon={
            <ReceiptLongIcon />
          }
          label="Total Generated"
          value={
            loadingRecords
              ? '…'
              : totalGenerated
          }
          color="#1976d2"
        />

        <StatCard
          icon={
            <AccountBalanceWalletIcon />
          }
          label="Total Fee Amount"
          value={
            loadingRecords
              ? '…'
              : `₹${totalFeeAmount.toLocaleString(
                  'en-IN',
                  {
                    minimumFractionDigits: 2,
                  }
                )}`
          }
          color="#2e7d32"
        />

        <StatCard
          icon={
            <TrendingUpIcon />
          }
          label="Generated Today"
          value={
            loadingRecords
              ? '…'
              : todayCount
          }
          color="#ed6c02"
        />
      </Box>


      {/* ======================================================
          FORM
      ====================================================== */}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor:
            'divider',
          borderRadius: 2,
          p: {
            xs: 2.5,
            sm: 3.5,
          },
          mb: 3,
        }}
      >
        <SectionLabel
          icon={
            <PersonSearchIcon />
          }
          text="Candidate & Date"
        />

        <FormRow
          label="Candidate Name"
          required
        >
          <TextField
            fullWidth
            size="small"
            value={
              displayCandidateName
            }
            onClick={
              !isEditing
                ? () =>
                    setShowCandidateModal(
                      true
                    )
                : undefined
            }
            placeholder="Click to search and select candidate…"
            InputProps={{
              readOnly: true,

              disabled:
                isEditing,

              sx: {
                cursor: isEditing
                  ? 'not-allowed'
                  : 'pointer',
              },

              endAdornment:
                !isEditing && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={() =>
                        setShowCandidateModal(
                          true
                        )
                      }
                      sx={{
                        height: 28,
                        borderRadius:
                          '4px',
                        textTransform:
                          'none',
                        fontSize:
                          '0.75rem',
                        fontWeight: 600,
                        px: 1.5,
                        gap: 0.5,
                      }}
                    >
                      <SearchIcon
                        sx={{
                          fontSize: 14,
                        }}
                      />
                      Search
                    </Button>
                  </InputAdornment>
                ),
            }}
            sx={inputSx}
          />
        </FormRow>


        {/* Selected candidate details */}

        {selectedCandidateDetails && (
          <Box
            sx={{
              mt: 1,
              mb: 2,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor:
                'background.paper',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                mb: 1,
              }}
            >
              Enrolled Courses
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection:
                  'column',
                gap: 0.8,
              }}
            >
              {selectedCandidateDetails?.courses?.map(
                (
                  course,
                  index
                ) => (
                  <Box
                    key={index}
                    sx={{
                      display:
                        'flex',
                      justifyContent:
                        'space-between',
                      alignItems:
                        'center',
                      gap: 2,
                      p: 1,
                      mb: 1,
                      borderRadius: 1,
                      backgroundColor:
                        'action.hover',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {
                          course.course_name
                        }
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {
                          course.course_code
                        }
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        whiteSpace:
                          'nowrap',
                      }}
                    >
                      ₹
                      {Number(
                        course.total_course_fee ||
                          0
                      ).toLocaleString(
                        'en-IN',
                        {
                          minimumFractionDigits: 2,
                        }
                      )}
                    </Typography>
                  </Box>
                )
              )}
            </Box>

            <Box
              sx={{
                mt: 1,
                pt: 1,
                display: 'flex',
                justifyContent:
                  'space-between',
                alignItems:
                  'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                }}
              >
                Total Course Fee
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                }}
              >
                ₹
                {Number(
                  selectedCandidateDetails?.total_course_fee ||
                    0
                ).toLocaleString(
                  'en-IN',
                  {
                    minimumFractionDigits: 2,
                  }
                )}
              </Typography>
            </Box>
          </Box>
        )}

        {selectedCandidateDetails?.courses?.length > 0 && (
          <FormRow
            label="Courses for Fee Generation"
            required
          >
            <FormControl fullWidth size="small">
              <Select
                multiple
                open={courseSelectOpen}
                onOpen={() => setCourseSelectOpen(true)}
                onClose={() => setCourseSelectOpen(false)}
                value={selectedCourseIds}
                displayEmpty
                disabled={isEditing}
                onChange={(event) => {
                  const rawValue = event.target.value || [];
                  const allCourseIds = selectedCandidateDetails.courses
                    .map((course) => String(course.course_id ?? course.course ?? course.id ?? ''))
                    .filter(Boolean);
                  let nextCourseIds = rawValue.map(String);

                  if (nextCourseIds.includes('__ok__')) {
                    setCourseSelectOpen(false);
                    return;
                  }

                  if (nextCourseIds.includes('__all_courses__')) {
                    nextCourseIds = selectedCourseIds.length === allCourseIds.length
                      ? []
                      : allCourseIds;
                  } else {
                    // Course names can repeat in the API. Treat every course
                    // with the same name as one selectable dropdown option.
                    const changedId = nextCourseIds.find((id) => !selectedCourseIds.includes(id))
                      || selectedCourseIds.find((id) => !nextCourseIds.includes(id));
                    const changedCourse = selectedCandidateDetails.courses.find((course) =>
                      String(course.course_id ?? course.course ?? course.id ?? '') === String(changedId)
                    );
                    if (changedCourse) {
                      const changedName = String(changedCourse.course_name || changedCourse.name || '').trim().toLowerCase();
                      const sameNameIds = selectedCandidateDetails.courses
                        .filter((course) => String(course.course_name || course.name || '').trim().toLowerCase() === changedName)
                        .map((course) => String(course.course_id ?? course.course ?? course.id ?? ''))
                        .filter(Boolean);
                      const isGroupSelected = sameNameIds.some((id) => selectedCourseIds.includes(id));
                      if (isGroupSelected) {
                        nextCourseIds = nextCourseIds.filter((id) => !sameNameIds.includes(id));
                      } else {
                        nextCourseIds = [...new Set([...nextCourseIds, ...sameNameIds])];
                      }
                    }
                  }

                  const firstSelectedId = nextCourseIds[0] || '';
                  const firstSelectedCourse = selectedCandidateDetails.courses.find((course) =>
                    String(course.course_id ?? course.course ?? course.id ?? '') === firstSelectedId
                  );
                  setSelectedCourseIds(nextCourseIds);
                  setSelectedCourseId(firstSelectedId);
                  setCourseFee(firstSelectedCourse ? String(Number(firstSelectedCourse.total_course_fee) || 0) : '');
                  setExtraAmount('');
                  setDiscount('');
                  setKitCharges('');
                  setGstPercentage('');
                  setAdvanceAmount('');
                  setPaymentMode('');
                  setSelectedMonths([]);
                  setMonthlyTotalAmount('');
                  setInstallmentCount('1');
                  setInstallmentRows([]);
                }}
                renderValue={(selected) => {
                  const allCourseIds = selectedCandidateDetails.courses
                    .map((course) => String(course.course_id ?? course.course ?? course.id ?? ''))
                    .filter(Boolean);
                  if (!selected.length) return <em>Select courses</em>;
                  if (selected.length === allCourseIds.length) return 'All Courses';
                  return [...new Set(selectedCandidateDetails.courses
                    .filter((course) => selected.includes(String(course.course_id ?? course.course ?? course.id ?? '')))
                    .map((course) => course.course_name || course.name || `Course ${course.course_id}`))]
                    .join(', ');
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 360 },
                  },
                }}
                sx={selectSx}
              >
                <MenuItem value="__all_courses__">
                  <Checkbox
                    checked={selectedCourseIds.length === selectedCandidateDetails.courses
                      .map((course) => String(course.course_id ?? course.course ?? course.id ?? '')).filter(Boolean).length}
                    sx={{ mr: 1 }}
                  />
                  All Courses
                </MenuItem>
                {[...new Map(selectedCandidateDetails.courses.map((course, index) => {
                  const name = String(course.course_name || course.name || `Course ${index + 1}`).trim();
                  const key = name.toLowerCase();
                  return [key, { name, courses: selectedCandidateDetails.courses.filter((item) =>
                    String(item.course_name || item.name || '').trim().toLowerCase() === key
                  ) }];
                })).values()].map((group, index) => {
                  const ids = group.courses.map((course) => String(course.course_id ?? course.course ?? course.id ?? '')).filter(Boolean);
                  const groupFee = group.courses.reduce((sum, course) => sum + (Number(course.total_course_fee) || 0), 0);
                  const allGroupSelected = ids.length > 0 && ids.every((id) => selectedCourseIds.includes(id));
                  return (
                    <MenuItem key={`${group.name}-${index}`} value={ids[0] || ''}>
                      <Checkbox checked={allGroupSelected} indeterminate={!allGroupSelected && ids.some((id) => selectedCourseIds.includes(id))} sx={{ mr: 1 }} />
                      {group.name}
                      {' — '}
                      ₹{groupFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      {ids.length > 1 ? ` (${ids.length} entries totalled)` : ''}
                    </MenuItem>
                  );
                })}
                <Divider />
                <MenuItem
                  value="__ok__"
                  sx={{ justifyContent: 'center', fontWeight: 700, color: 'primary.main' }}
                >
                  OK
                </MenuItem>
              </Select>
            </FormControl>
          </FormRow>
        )}

        {isEditing &&
          displayCandidateName && (
            <Box
              sx={{
                ml: '180px',
                mb: 2,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor:
                  theme.palette
                    .action.selected,
                border: '1px solid',
                borderColor:
                  'divider',
                display: 'flex',
                gap: 2,
                flexWrap:
                  'wrap',
                alignItems:
                  'center',
              }}
            >
              <Avatar
                sx={{
                  bgcolor:
                    'warning.main',
                  width: 28,
                  height: 28,
                  fontSize:
                    '0.75rem',
                  fontWeight: 700,
                }}
              >
                {(
                  displayCandidateName[0] ||
                  'C'
                ).toUpperCase()}
              </Avatar>

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                }}
              >
                {
                  displayCandidateName
                }
              </Typography>

              <Chip
                label="Editing"
                size="small"
                color="warning"
                sx={{
                  height: 20,
                  fontSize:
                    '0.68rem',
                }}
              />
            </Box>
          )}


        <Divider
          sx={{
            my: 2.5,
          }}
        />


        {/* ====================================================
            GENERATED DATE
        ==================================================== */}

        <FormRow
          label="Generated Date"
          required
        >
          <TextField
            size="small"
            type="date"
            fullWidth
            value={
              generatedDate
            }
            onChange={(e) =>
              setGeneratedDate(
                e.target.value
              )
            }
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon
                    sx={{
                      fontSize: 15,
                      color:
                        isDarkMode
                          ? '#fff'
                          : 'text.primary',
                    }}
                  />
                </InputAdornment>
              ),

              endAdornment: (
                <InputAdornment position="end">
                  <CalendarTodayIcon
                    sx={{
                      fontSize: 17,
                      color:
                        isDarkMode
                          ? '#fff'
                          : 'text.primary',
                      cursor:
                        'pointer',
                    }}
                    onClick={(
                      e
                    ) => {
                      const input =
                        e.currentTarget
                          .closest(
                            '.MuiTextField-root'
                          )
                          ?.querySelector(
                            "input[type='date']"
                          );

                      if (
                        input?.showPicker
                      ) {
                        input.showPicker();
                      } else {
                        input?.focus();
                      }
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              ...inputSx,

              '& input[type="date"]':
                {
                  colorScheme:
                    isDarkMode
                      ? 'dark'
                      : 'light',

                  color:
                    isDarkMode
                      ? '#fff'
                      : 'inherit',

                  WebkitTextFillColor:
                    isDarkMode
                      ? '#fff'
                      : 'inherit',
                },

              '& input[type="date"]::-webkit-calendar-picker-indicator':
                {
                  opacity: 0,
                  position:
                    'absolute',
                  right: 0,
                  width: '40px',
                  height: '100%',
                  cursor:
                    'pointer',
                },
            }}
          />
        </FormRow>


        <Divider
          sx={{
            my: 2.5,
          }}
        />


        {/* ====================================================
            FEE TYPE
        ==================================================== */}

        <SectionLabel
          icon={
            <PaymentIcon />
          }
          text="Fee Type"
        />

        <FormRow
          label="Fee Type"
          required
        >
          <FormControl
            fullWidth
            size="small"
          >
            <Select
              value={
                feeType
              }
              displayEmpty
              onChange={(e) => {
                const value =
                  e.target.value;

                setFeeType(value);

                setSelectedMonths(
                  []
                );

                setInstallmentCount(
  value === 'Installment' ? '1' : ''
);

                setInstallmentRows(
                  []
                );

                if (
                  value ===
                  'Monthly'
                ) {
                  setExtraAmount(
                    ''
                  );

                  setDiscount(
                    ''
                  );

                  setKitCharges(
                    ''
                  );

                  setGstPercentage(
                    ''
                  );
                }
              }}
              sx={selectSx}
            >
              <MenuItem value="">
                <em
                  style={{
                    color:
                      '#9e9e9e',
                  }}
                >
                  — Select Fee Type —
                </em>
              </MenuItem>

              {feeTypeData.map(
                (item) => (
                  <MenuItem
                    key={
                      item.id
                    }
                    value={
                      item.name
                    }
                  >
                    {
                      item.name
                    }
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </FormRow>


        {/* ====================================================
            INSTALLMENT DETAILS
        ==================================================== */}

        {feeType ===
          'Installment' && (
          <>
            <Divider
              sx={{
                my: 2.5,
              }}
            />

            <SectionLabel
              icon={
                <ReceiptLongIcon />
              }
              text="Installment Fee Details"
            />

            <FormRow
              label="Course Fee"
              required
              helperText="Auto-calculated from the selected course"
            >
              <TextField
                size="small"
                type="number"
                fullWidth
                value={
                  courseFee
                }
                InputProps={{
                  readOnly: true,

                  startAdornment:
                    (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              '0.85rem',
                            color:
                              'text.disabled',
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                }}
                sx={{
                  ...inputSx,

                  '& .MuiOutlinedInput-root':
                    {
                      ...inputSx[
                        '& .MuiOutlinedInput-root'
                      ],

                      bgcolor:
                        theme.palette
                          .action
                          .hover,
                    },
                }}
              />
            </FormRow>

            <FormRow label="Kit Charges">
              <TextField
                size="small"
                type="number"
                fullWidth
                value={
                  kitCharges
                }
                onChange={(e) =>
                  setKitCharges(
                    e.target.value
                  )
                }
                placeholder="0.00"
                InputProps={{
                  startAdornment:
                    (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              '0.85rem',
                            color:
                              'text.disabled',
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                }}
                sx={inputSx}
              />
            </FormRow>

            <FormRow label="Extra Amount">
              <TextField
                size="small"
                type="number"
                fullWidth
                value={
                  extraAmount
                }
                onChange={(e) =>
                  setExtraAmount(
                    e.target.value
                  )
                }
                placeholder="0.00"
                InputProps={{
                  startAdornment:
                    (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              '0.85rem',
                            color:
                              'text.disabled',
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                }}
                sx={inputSx}
              />
            </FormRow>

            <FormRow label="Discount">
              <TextField
                size="small"
                type="number"
                fullWidth
                value={
                  discount
                }
                onChange={(e) =>
                  setDiscount(
                    e.target.value
                  )
                }
                placeholder="0.00"
                InputProps={{
                  startAdornment:
                    (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              '0.85rem',
                            color:
                              'text.disabled',
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                }}
                sx={inputSx}
              />
            </FormRow>

            <FormRow label="Billing Before GST">
              <TextField
                size="small"
                type="number"
                fullWidth
                value={
                  billingBeforeGST
                }
                InputProps={{
                  readOnly: true,

                  startAdornment:
                    (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              '0.85rem',
                            color:
                              'primary.main',
                            fontWeight: 600,
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                }}
                sx={{
                  ...inputSx,

                  '& .MuiOutlinedInput-root':
                    {
                      ...inputSx[
                        '& .MuiOutlinedInput-root'
                      ],

                      bgcolor:
                        theme.palette
                          .action
                          .hover,
                    },

                  '& .MuiOutlinedInput-input':
                    {
                      fontWeight: 600,
                      color:
                        theme.palette
                          .primary
                          .main,
                    },
                }}
              />
            </FormRow>

            <FormRow label="GST Percentage">
              <FormControl
                fullWidth
                size="small"
              >
                <Select
                  value={
                    gstPercentage
                  }
                  displayEmpty
                  onChange={(e) =>
                    setGstPercentage(
                      e.target.value
                    )
                  }
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em
                      style={{
                        color:
                          '#9e9e9e',
                      }}
                    >
                      — Select GST —
                    </em>
                  </MenuItem>

                  {gstData.map(
                    (item) => (
                      <MenuItem
                        key={
                          item.id
                        }
                        value={
                          item.id
                        }
                      >
                        {
                          item.name
                        }
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </FormRow>

            <FormRow label="Total Fee (incl. GST)">
              <TextField
                size="small"
                type="number"
                fullWidth
                value={
                  totalFee
                }
                InputProps={{
                  readOnly: true,

                  startAdornment:
                    (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              '0.85rem',
                            color:
                              'success.main',
                            fontWeight: 700,
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                }}
                sx={{
                  ...inputSx,

                  '& .MuiOutlinedInput-root':
                    {
                      ...inputSx[
                        '& .MuiOutlinedInput-root'
                      ],

                      bgcolor:
                        theme.palette
                          .action
                          .hover,
                    },

                  '& .MuiOutlinedInput-input':
                    {
                      fontWeight: 700,
                      fontSize:
                        '1rem',
                      color:
                        theme.palette
                          .success
                          .main,
                    },
                }}
              />
            </FormRow>

           <FormRow
  label="Installment Count"
  required
  helperText="Maximum 12 installments allowed"
>
              <TextField
  size="small"
  type="number"
  fullWidth
  value={
    installmentCount
  }
  onChange={
    handleInstallmentCountChange
  }
  onBlur={() => {
    if (!installmentCount) setInstallmentCount('1');
  }}
  placeholder="Number of installments"
                inputProps={{
                  min: 1,
                  max: 12,
                  step: 1,
                }}
                sx={inputSx}
              />
            </FormRow>
          </>
        )}


        {/* ====================================================
            MONTHLY
        ==================================================== */}

        {feeType ===
          'Monthly' && (
          <>
            <Divider
              sx={{
                my: 2.5,
              }}
            />

            <SectionLabel
              icon={
                <EventIcon />
              }
              text="Monthly Fee Details"
            />

            <FormRow
              label="Select Months"
              required
            >
              <Box>
                <Box
                  sx={{
                    display:
                      'flex',
                    flexWrap:
                      'wrap',
                    gap: 1,
                  }}
                >
                  {availableMonths.map(
                    (
                      monthObj,
                      index
                    ) => {
                      const isSelected =
                        !!selectedMonths.find(
                          (
                            month
                          ) =>
                            month.month ===
                              monthObj.month &&
                            month.year ===
                              monthObj.year
                        );

                      return (
                        <Chip
                          key={
                            index
                          }
                          label={`${monthObj.month} ${monthObj.year}`}
                          onClick={() =>
                            handleMonthToggle(
                              monthObj
                            )
                          }
                          color={
                            isSelected
                              ? 'primary'
                              : 'default'
                          }
                          variant={
                            isSelected
                              ? 'filled'
                              : 'outlined'
                          }
                          clickable
                          sx={{
                            fontWeight:
                              isSelected
                                ? 700
                                : 400,

                            fontSize:
                              '0.78rem',

                            height: 28,

                            borderRadius:
                              '6px',

                            backgroundColor:
                              isSelected
                                ? theme
                                    .palette
                                    .primary
                                    .main
                                : 'transparent',

                            color:
                              isSelected
                                ? '#fff'
                                : theme
                                    .palette
                                    .text
                                    .primary,

                            borderColor:
                              isSelected
                                ? theme
                                    .palette
                                    .primary
                                    .main
                                : theme
                                    .palette
                                    .divider,

                            '&:hover':
                              {
                                backgroundColor:
                                  isSelected
                                    ? theme
                                        .palette
                                        .primary
                                        .dark
                                    : theme
                                        .palette
                                        .action
                                        .hover,

                                transform:
                                  'translateY(-1px)',
                              },

                            transition:
                              'all 0.15s',
                          }}
                        />
                      );
                    }
                  )}
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    display:
                      'block',

                    color:
                      selectedMonths.length >
                      0
                        ? 'primary.main'
                        : 'text.disabled',

                    fontWeight:
                      selectedMonths.length >
                      0
                        ? 600
                        : 400,
                  }}
                >
                  {selectedMonths.length >
                  0
                    ? `${selectedMonths.length} month(s) selected · ₹${monthlyTotalAmount} ÷ ${selectedMonths.length} = ₹${(
                        Number(
                          monthlyTotalAmount
                        ) /
                        selectedMonths.length
                      ).toFixed(
                        2
                      )} per month`
                    : 'No months selected'}
                </Typography>
              </Box>
            </FormRow>

            <FormRow
              label="Total Course Fee"
              helperText={`₹${
                courseFee || 0
              } split across ${
                selectedMonths.length
              } month(s)`}
            >
              <TextField
                size="small"
                type="number"
                fullWidth
                value={
                  monthlyTotalAmount
                }
                InputProps={{
                  readOnly: true,

                  startAdornment:
                    (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              '0.85rem',
                            color:
                              'success.main',
                            fontWeight: 700,
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                }}
                sx={{
                  ...inputSx,

                  '& .MuiOutlinedInput-root':
                    {
                      ...inputSx[
                        '& .MuiOutlinedInput-root'
                      ],

                      bgcolor:
                        theme.palette
                          .action
                          .hover,
                    },

                  '& .MuiOutlinedInput-input':
                    {
                      fontWeight: 700,
                      fontSize:
                        '1rem',
                      color:
                        theme.palette
                          .success
                          .main,
                    },
                }}
              />
            </FormRow>
          </>
        )}


        {/* ====================================================
            INSTALLMENT SCHEDULE
        ==================================================== */}

        {installmentRows.length >
          0 && (
          <>
            <Divider
              sx={{
                my: 2.5,
              }}
            />

            <SectionLabel
              icon={
                <ReceiptLongIcon />
              }
              text="Installment Schedule"
            />

            <Box
              sx={{
                ml: '180px',
                mb: 2,
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  overflow: 'hidden',
                }}
              >
                <Table
                  size="small"
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor:
                          theme.palette
                            .background
                            .default,
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize:
                            '0.72rem',
                          textTransform:
                            'uppercase',
                          letterSpacing: 0.5,
                          color:
                            'primary.main',
                          py: 1,
                          width: 80,
                        }}
                      >
                        Inst. #
                      </TableCell>

                      {feeType ===
                        'Monthly' && (
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            fontSize:
                              '0.72rem',
                            textTransform:
                              'uppercase',
                            letterSpacing: 0.5,
                            color:
                              'primary.main',
                            py: 1,
                          }}
                        >
                          Month
                        </TableCell>
                      )}

                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize:
                            '0.72rem',
                          textTransform:
                            'uppercase',
                          letterSpacing: 0.5,
                          color:
                            'primary.main',
                          py: 1,
                        }}
                      >
                        Amount (₹) *
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize:
                            '0.72rem',
                          textTransform:
                            'uppercase',
                          letterSpacing: 0.5,
                          color:
                            'primary.main',
                          py: 1,
                        }}
                      >
                        Due Date *
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {installmentRows.map(
                      (
                        row,
                        index
                      ) => {
                        const monthObj =
                          feeType ===
                          'Monthly'
                            ? selectedMonths[
                                index
                              ]
                            : null;

                        return (
                          <TableRow
                            key={
                              index
                            }
                            sx={{
                              '&:last-child td':
                                {
                                  border: 0,
                                },
                            }}
                          >
                            <TableCell
                              sx={{
                                py: 0.8,
                                fontSize:
                                  '0.82rem',
                                fontWeight: 600,
                                color:
                                  'primary.main',
                              }}
                            >
                              #
                              {
                                row.installment_no
                              }
                            </TableCell>

                            {feeType ===
                              'Monthly' && (
                              <TableCell
                                sx={{
                                  py: 0.8,
                                  fontSize:
                                    '0.82rem',
                                }}
                              >
                                {monthObj
                                  ? `${monthObj.month} ${monthObj.year}`
                                  : '—'}
                              </TableCell>
                            )}

                            <TableCell
                              sx={{
                                py: 0.8,
                              }}
                            >
                              <TextField
                                size="small"
                                type="number"
                                value={
                                  row.amount
                                }
                                onChange={(
                                  e
                                ) =>
                                  updateInstallmentRow(
                                    index,
                                    'amount',
                                    e.target
                                      .value
                                  )
                                }
                                disabled={
                                  feeType ===
                                  'Monthly'
                                }
                                inputProps={{
                                  min: 0,
                                  step: '0.01',
                                  style:
                                    {
                                      padding:
                                        '4px 8px',
                                      fontSize:
                                        '0.85rem',
                                    },
                                }}
                                sx={{
                                  width: 130,

                                  '& .MuiOutlinedInput-root':
                                    {
                                      backgroundColor:
                                        'background.paper',

                                      borderRadius:
                                        '5px',

                                      height: 30,

                                      bgcolor:
                                        feeType ===
                                        'Monthly'
                                          ? theme
                                              .palette
                                              .action
                                              .hover
                                          : 'background.paper',
                                    },
                                }}
                              />
                            </TableCell>

                            <TableCell
                              sx={{
                                py: 0.8,
                              }}
                            >
                              <TextField
                                size="small"
                                type="date"
                                value={
                                  row.due_date
                                }
                                onChange={(
                                  e
                                ) =>
                                  updateInstallmentRow(
                                    index,
                                    'due_date',
                                    e.target
                                      .value
                                  )
                                }
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                inputProps={{
                                  style:
                                    {
                                      padding:
                                        '4px 8px',
                                      fontSize:
                                        '0.85rem',
                                    },
                                }}
                                sx={{
                                  width: 160,

                                  '& .MuiOutlinedInput-root':
                                    {
                                      borderRadius:
                                        '5px',
                                      height: 30,
                                    },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )}
                  </TableBody>
                </Table>

                {feeType ===
                  'Installment' &&
                  installmentRows.length >
                    0 &&
                  (() => {
                    const rowSum =
                      installmentRows.reduce(
                        (
                          sum,
                          row
                        ) =>
                          sum +
                          Number(
                            row.amount ||
                              0
                          ),
                        0
                      );

                    const diff =
                      Math.abs(
                        rowSum -
                          Number(
                            totalFee
                          )
                      );

                    const isMatch =
                      diff <= 1;

                    return (
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          borderTop:
                            '2px solid',
                          borderColor:
                            'divider',
                          display:
                            'flex',
                          justifyContent:
                            'space-between',
                          alignItems:
                            'center',
                          bgcolor:
                            isMatch
                              ? 'rgba(46,125,50,0.05)'
                              : 'rgba(211,47,47,0.05)',
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color:
                              isMatch
                                ? 'success.main'
                                : 'error.main',
                          }}
                        >
                          {isMatch
                            ? '✓ Amounts balance correctly'
                            : `⚠ Sum ₹${rowSum.toFixed(
                                2
                              )} ≠ Total ₹${Number(
                                totalFee
                              ).toFixed(
                                2
                              )}`}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                          }}
                        >
                          Row Total: ₹
                          {rowSum.toLocaleString(
                            'en-IN',
                            {
                              minimumFractionDigits: 2,
                            }
                          )}
                        </Typography>
                      </Box>
                    );
                  })()}
              </Paper>
            </Box>
          </>
        )}


        {/* ====================================================
            PAYMENT
        ==================================================== */}

        {(feeType ===
          'Installment' ||
          feeType ===
            'Monthly') && (
          <>
            <Divider
              sx={{
                my: 2.5,
              }}
            />

            <SectionLabel
              icon={
                <AccountBalanceWalletIcon />
              }
              text="Advance Payment & Remarks"
            />

            <FormRow label="Advance Amount">
              <TextField
                size="small"
                type="number"
                fullWidth
                value={
                  advanceAmount
                }
                onChange={(e) =>
                  setAdvanceAmount(
                    e.target.value
                  )
                }
                placeholder="0.00"
                inputProps={{
                  min: 0,
                  step: '0.01',
                }}
                InputProps={{
                  startAdornment:
                    (
                      <InputAdornment position="start">
                        <Typography
                          sx={{
                            fontSize:
                              '0.85rem',
                            color:
                              'text.disabled',
                          }}
                        >
                          ₹
                        </Typography>
                      </InputAdornment>
                    ),
                }}
                sx={inputSx}
              />
            </FormRow>

            <FormRow label="Payment Mode">
              <FormControl
                fullWidth
                size="small"
                error={
                  !!paymentMethodsError
                }
                disabled={
                  loadingPaymentMethods
                }
              >
                <Select
                  value={
                    paymentMode
                  }
                  displayEmpty
                  onChange={(e) => {
                    setPaymentMode(
                      e.target.value
                    );

                    setLedger('');
                    setBankName('');
                    setReferenceNo('');
                    setReferenceDate(
                      ''
                    );
                  }}
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em
                      style={{
                        color:
                          '#9e9e9e',
                      }}
                    >
                      {loadingPaymentMethods
                        ? 'Loading…'
                        : '— Select Mode —'}
                    </em>
                  </MenuItem>

                  {paymentMethods.map(
                    (method) => (
                      <MenuItem
                        key={
                          method.id
                        }
                        value={
                          method.id
                        }
                      >
                        {method.name ||
                          method.payment_method ||
                          method.method_name ||
                          `Mode #${method.id}`}
                      </MenuItem>
                    )
                  )}
                </Select>

                {paymentMethodsError && (
                  <FormHelperText error>
                    {
                      paymentMethodsError
                    }
                  </FormHelperText>
                )}
              </FormControl>
            </FormRow>


            {/* BANK */}

            {paymentMode &&
              isBank(
                paymentMode
              ) && (
                <>
                  <FormRow
                    label="Ledger"
                    required
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={
                        ledger
                      }
                      onChange={(
                        e
                      ) =>
                        setLedger(
                          e.target
                            .value
                        )
                      }
                      placeholder="e.g. HDFC Ledger"
                      sx={inputSx}
                    />
                  </FormRow>

                  <FormRow
                    label="Bank Name"
                    required
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={
                        bankName
                      }
                      onChange={(
                        e
                      ) =>
                        setBankName(
                          e.target
                            .value
                        )
                      }
                      placeholder="e.g. HDFC Bank"
                      sx={inputSx}
                    />
                  </FormRow>

                  <FormRow
                    label="Reference No"
                    required
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={
                        referenceNo
                      }
                      onChange={(
                        e
                      ) =>
                        setReferenceNo(
                          e.target
                            .value
                        )
                      }
                      placeholder="Cheque / reference number"
                      sx={inputSx}
                    />
                  </FormRow>

                  <FormRow
                    label="Reference Date"
                    required
                  >
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={
                        referenceDate
                      }
                      onChange={(
                        e
                      ) =>
                        setReferenceDate(
                          e.target
                            .value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        startAdornment:
                          (
                            <InputAdornment position="start">
                              <CalendarTodayIcon
                                sx={{
                                  fontSize: 15,
                                  color:
                                    'text.disabled',
                                }}
                              />
                            </InputAdornment>
                          ),
                      }}
                      sx={inputSx}
                    />
                  </FormRow>
                </>
              )}


            {/* UPI */}

            {paymentMode &&
              isUpi(
                paymentMode
              ) && (
                <>
                  <FormRow
                    label="Reference No"
                    required
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={
                        referenceNo
                      }
                      onChange={(
                        e
                      ) =>
                        setReferenceNo(
                          e.target
                            .value
                        )
                      }
                      placeholder="UPI transaction / reference ID"
                      sx={inputSx}
                    />
                  </FormRow>

                  <FormRow
                    label="Reference Date"
                    required
                  >
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={
                        referenceDate
                      }
                      onChange={(
                        e
                      ) =>
                        setReferenceDate(
                          e.target
                            .value
                        )
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        startAdornment:
                          (
                            <InputAdornment position="start">
                              <CalendarTodayIcon
                                sx={{
                                  fontSize: 15,
                                  color:
                                    'text.disabled',
                                }}
                              />
                            </InputAdornment>
                          ),
                      }}
                      sx={inputSx}
                    />
                  </FormRow>
                </>
              )}


            <FormRow label="Remarks">
              <TextField
                fullWidth
                multiline
                rows={3}
                value={
                  remarks
                }
                onChange={(e) =>
                  setRemarks(
                    e.target.value
                  )
                }
                placeholder="Any additional notes…"
                sx={{
                  '& .MuiOutlinedInput-root':
                    {
                      borderRadius:
                        '6px',
                      fontSize:
                        '0.875rem',

                      '&:hover fieldset':
                        {
                          borderColor:
                            'primary.main',
                        },

                      '&.Mui-focused':
                        {
                          boxShadow:
                            '0 0 0 3px rgba(25,118,210,0.12)',
                        },
                    },
                }}
              />
            </FormRow>


            {/* BUTTONS */}

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent:
                  'flex-end',
                mt: 3,
                pt: 2,
                borderTop:
                  '1px solid',
                borderColor:
                  'divider',
              }}
            >
              <Button
                variant="outlined"
                onClick={
                  handleReset
                }
                sx={{
                  borderRadius:
                    '6px',
                  textTransform:
                    'none',
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Reset
              </Button>

              <Button
                variant="contained"
                disableElevation
                onClick={
                  handleSave
                }
                disabled={
                  !isFormFilled() ||
                  isSaving
                }
                sx={{
                  borderRadius:
                    '6px',
                  textTransform:
                    'none',
                  fontWeight: 700,
                  px: 4,
                  fontSize:
                    '0.9rem',

                  background:
                    isEditing
                      ? `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.main} 100%)`
                      : 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',

                  '&:hover':
                    {
                      background:
                        isEditing
                          ? theme
                              .palette
                              .warning
                              .dark
                          : '#0d47a1',
                    },
                }}
              >
                {isSaving ? (
                  <>
                    <CircularProgress
                      size={14}
                      color="inherit"
                      sx={{
                        mr: 1,
                      }}
                    />

                    Saving…
                  </>
                ) : isEditing ? (
                  <>
                    <EditIcon
                      sx={{
                        fontSize: 16,
                        mr: 0.8,
                      }}
                    />

                    Update
                  </>
                ) : (
                  <>
                    <AddCircleOutlineIcon
                      sx={{
                        fontSize: 16,
                        mr: 0.8,
                      }}
                    />

                    Save
                  </>
                )}
              </Button>
            </Box>
          </>
        )}
      </Paper>


      {/* ======================================================
          FEE RECORDS
      ====================================================== */}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            'space-between',
          mb: 1.5,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              lineHeight: 1.2,
            }}
          >
            Generated Fee Records
          </Typography>

          {!loadingRecords && (
            <Typography
              variant="caption"
              color="text.disabled"
            >
              {feeRecords.length}{' '}
              record
              {feeRecords.length !==
              1
                ? 's'
                : ''}{' '}
              total
            </Typography>
          )}
        </Box>

        {selectedRecords.length >
          0 && (
          <Button
            variant="contained"
            color="error"
            disableElevation
            startIcon={
              <DeleteIcon />
            }
            onClick={() =>
              setDeleteDialogOpen(
                true
              )
            }
            sx={{
              borderRadius:
                '6px',
              textTransform:
                'none',
              fontWeight: 600,
            }}
          >
            Delete Selected (
            {
              selectedRecords.length
            }
            )
          </Button>
        )}
      </Box>


      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor:
            'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <CustomTableScrollbar>
          <TableContainer>
            <Table
              stickyHeader
              size="small"
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      bgcolor:
                        theme.palette
                          .background
                          .default,
                    }}
                  >
                    <Checkbox
                      indeterminate={
                        selectedRecords.length >
                          0 &&
                        selectedRecords.length <
                          feeRecords.length
                      }
                      checked={
                        feeRecords.length >
                          0 &&
                        selectedRecords.length ===
                          feeRecords.length
                      }
                      onChange={
                        handleSelectAllClick
                      }
                      size="small"
                    />
                  </TableCell>

                  <TableCell
                    sx={{
                      bgcolor: theme.palette.background.default,
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'primary.main',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    S.No
                  </TableCell>

                  {[
                    'Candidate',
                    'Adm. No',
                    'Fee Type',
                    'Course',
                    'Generated Date',
                    'Course Fee',
                    'Extra',
                    'Discount',
                    'Kit Charges',
                    'Total Amount',
                    'Advance',
                    'Balance Due',
                    'Paid',
                    'Pending',
                    'Installments',
                    'Actions',
                  ].map(
                    (
                      heading,
                      index
                    ) => (
                      <TableCell
                        key={
                          heading
                        }
                        align={
                          index ===
                          15
                            ? 'center'
                            : 'left'
                        }
                        sx={{
                          bgcolor:
                            theme.palette
                              .background
                              .default,

                          fontWeight: 700,

                          fontSize:
                            '0.72rem',

                          textTransform:
                            'uppercase',

                          letterSpacing: 0.5,

                          color:
                            'primary.main',

                          whiteSpace:
                            'nowrap',

                          py: 1.5,

                          borderBottom:
                            '2px solid',

                          borderColor:
                            'divider',
                        }}
                      >
                        {
                          heading
                        }
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>


              <TableBody>
                {loadingRecords ? (
                  <TableRow>
                    <TableCell
                      colSpan={18}
                      align="center"
                      sx={{
                        py: 6,
                      }}
                    >
                      <CircularProgress
                        size={28}
                      />

                      <Typography
                        variant="body2"
                        color="text.disabled"
                        sx={{
                          mt: 1,
                        }}
                      >
                        Loading records…
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : feeRecords.length ===
                  0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={18}
                      align="center"
                      sx={{
                        py: 6,
                      }}
                    >
                      <ReceiptLongIcon
                        sx={{
                          fontSize: 40,
                          color:
                            'text.disabled',
                          mb: 1,
                          display:
                            'block',
                          mx: 'auto',
                        }}
                      />

                      <Typography
                        color="text.disabled"
                        variant="body2"
                      >
                        No fee records generated yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  feeRecords
                    .slice(
                      page *
                        rowsPerPage,

                      page *
                        rowsPerPage +
                        rowsPerPage
                    )
                    .map(
                      (
                        record,
                        index
                      ) => {
                        const tc =
                          feeTypeColor(
                            record.fee_type
                          );

                        const installmentCount =
                          record.fee_type ===
                          'Monthly'
                            ? record
                                .monthly_details
                                ?.months_count ??
                              '—'
                            : record
                                .installments
                                ?.length ??
                              '—';

                        const totalAmount =
                          Number(
                            record.amount ??
                              0
                          );

                        const advanceAmt =
                          Number(
                            record.advance_amount ??
                              0
                          );

                        const balanceAmt =
                          Number(
                            record.balance_amount ??
                              0
                          );

                        const totalPaid =
                          Number(
                            record.total_paid ??
                              0
                          );

                        const pendingAmt =
                          Number(
                            record.pending_amount ??
                              0
                          );

                        return (
                          <TableRow
                            key={
                              record.fee_id
                            }
                            hover
                            sx={{
                              '&:last-child td':
                                {
                                  border: 0,
                                },

                              '&:hover':
                                {
                                  bgcolor:
                                    theme
                                      .palette
                                      .background
                                      .default,
                                },
                            }}
                          >
                            {/* CHECKBOX */}

                            <TableCell
                              padding="checkbox"
                            >
                              <Checkbox
                                checked={
                                  selectedRecords.indexOf(
                                    record.fee_id
                                  ) !==
                                  -1
                                }
                                onChange={() =>
                                  handleSelectRecordClick(
                                    record.fee_id
                                  )
                                }
                                size="small"
                              />
                            </TableCell>

                            <TableCell>
                              {page * rowsPerPage + index + 1}
                            </TableCell>

                            {/* CANDIDATE */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                minWidth: 130,
                              }}
                            >
                              <Box
                                sx={{
                                  display:
                                    'flex',
                                  alignItems:
                                    'center',
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    width: 26,
                                    height: 26,
                                    bgcolor:
                                      'primary.main',
                                    fontSize:
                                      '0.7rem',
                                    fontWeight: 700,
                                    flexShrink: 0,
                                  }}
                                >
                                  {(
                                    record.candidate_name?.[0] ||
                                    'C'
                                  ).toUpperCase()}
                                </Avatar>

                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize:
                                        '0.875rem',
                                      fontWeight: 600,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {
                                      record.candidate_name ||
                                      'N/A'
                                    }
                                  </Typography>

                                  {record.father_name && (
                                    <Typography
                                      sx={{
                                        fontSize:
                                          '0.7rem',
                                        color:
                                          'text.disabled',
                                        lineHeight: 1,
                                      }}
                                    >
                                      {
                                        record.father_name
                                      }
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>


                            {/* ADMISSION */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                fontFamily:
                                  'monospace',
                                fontSize:
                                  '0.8rem',
                                whiteSpace:
                                  'nowrap',
                                color:
                                  'text.disabled',
                              }}
                            >
                              {
                                record.admission_no ||
                                '—'
                              }
                            </TableCell>


                            {/* FEE TYPE */}

                            <TableCell
                              sx={{
                                py: 1.2,
                              }}
                            >
                              <Chip
                                label={
                                  record.fee_type
                                }
                                size="small"
                                color={
                                  tc.color
                                }
                                variant="outlined"
                                sx={{
                                  fontWeight: 600,
                                  fontSize:
                                    '0.72rem',
                                  height: 22,
                                  borderRadius:
                                    '4px',
                                }}
                              />
                            </TableCell>


                            {/* COURSE */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                minWidth: 130,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize:
                                    '0.82rem',
                                  fontWeight: 500,
                                }}
                              >
                                {
                                  record.course_name ||
                                  '—'
                                }
                              </Typography>

                              <Typography
                                sx={{
                                  fontSize:
                                    '0.7rem',
                                  color:
                                    'text.disabled',
                                  fontFamily:
                                    'monospace',
                                }}
                              >
                                {
                                  record.course_code ||
                                  ''
                                }
                              </Typography>
                            </TableCell>


                            {/* GENERATED DATE */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                fontSize:
                                  '0.82rem',
                                whiteSpace:
                                  'nowrap',
                                color:
                                  'text.disabled',
                              }}
                            >
                              {
                                record.generated_date ||
                                '—'
                              }
                            </TableCell>


                            {/* COURSE FEE */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                fontSize:
                                  '0.82rem',
                                whiteSpace:
                                  'nowrap',
                              }}
                            >
                              ₹
                              {Number(
                                record.course_fee ??
                                  0
                              ).toFixed(
                                2
                              )}
                            </TableCell>


                            {/* EXTRA */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                fontSize:
                                  '0.82rem',
                                whiteSpace:
                                  'nowrap',
                              }}
                            >
                              ₹
                              {Number(
                                record.extra_amount ??
                                  0
                              ).toFixed(
                                2
                              )}
                            </TableCell>


                            {/* DISCOUNT */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                fontSize:
                                  '0.82rem',
                                whiteSpace:
                                  'nowrap',
                              }}
                            >
                              {Number(
                                record.discount ??
                                  0
                              ) > 0 ? (
                                <Typography
                                  sx={{
                                    fontSize:
                                      '0.82rem',
                                    color:
                                      'error.main',
                                    fontWeight: 600,
                                  }}
                                >
                                  −₹
                                  {Number(
                                    record.discount
                                  ).toFixed(
                                    2
                                  )}
                                </Typography>
                              ) : (
                                '₹0.00'
                              )}
                            </TableCell>


                            {/* KIT */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                fontSize:
                                  '0.82rem',
                                whiteSpace:
                                  'nowrap',
                              }}
                            >
                              ₹
                              {Number(
                                record.kit_charges ??
                                  0
                              ).toFixed(
                                2
                              )}
                            </TableCell>


                            {/* TOTAL */}

                            <TableCell
                              sx={{
                                py: 1.2,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color:
                                    'primary.main',
                                  fontSize:
                                    '0.88rem',
                                  whiteSpace:
                                    'nowrap',
                                }}
                              >
                                ₹
                                {totalAmount.toLocaleString(
                                  'en-IN',
                                  {
                                    minimumFractionDigits: 2,
                                  }
                                )}
                              </Typography>
                            </TableCell>


                            {/* ADVANCE */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                fontSize:
                                  '0.82rem',
                                whiteSpace:
                                  'nowrap',
                              }}
                            >
                              {advanceAmt >
                              0 ? (
                                <Typography
                                  sx={{
                                    fontSize:
                                      '0.82rem',
                                    color:
                                      'info.main',
                                    fontWeight: 600,
                                  }}
                                >
                                  ₹
                                  {advanceAmt.toLocaleString(
                                    'en-IN',
                                    {
                                      minimumFractionDigits: 2,
                                    }
                                  )}
                                </Typography>
                              ) : (
                                '₹0.00'
                              )}
                            </TableCell>


                            {/* BALANCE */}

                            <TableCell
                              sx={{
                                py: 1.2,
                              }}
                            >
                              {balanceAmt <=
                              0 ? (
                                <Chip
                                  label="Cleared"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize:
                                      '0.7rem',
                                    height: 20,
                                    borderRadius:
                                      '4px',
                                  }}
                                />
                              ) : (
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize:
                                      '0.82rem',
                                    whiteSpace:
                                      'nowrap',
                                    color:
                                      'error.main',
                                  }}
                                >
                                  ₹
                                  {balanceAmt.toLocaleString(
                                    'en-IN',
                                    {
                                      minimumFractionDigits: 2,
                                    }
                                  )}
                                </Typography>
                              )}
                            </TableCell>


                            {/* PAID */}

                            <TableCell
                              sx={{
                                py: 1.2,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize:
                                    '0.82rem',
                                  whiteSpace:
                                    'nowrap',
                                  color:
                                    'success.main',
                                }}
                              >
                                ₹
                                {totalPaid.toLocaleString(
                                  'en-IN',
                                  {
                                    minimumFractionDigits: 2,
                                  }
                                )}
                              </Typography>
                            </TableCell>


                            {/* PENDING */}

                            <TableCell
                              sx={{
                                py: 1.2,
                              }}
                            >
                              {pendingAmt <=
                              0 ? (
                                <Typography
                                  sx={{
                                    fontSize:
                                      '0.82rem',
                                    color:
                                      'success.main',
                                    fontWeight: 600,
                                  }}
                                >
                                  ₹0.00
                                </Typography>
                              ) : (
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize:
                                      '0.82rem',
                                    whiteSpace:
                                      'nowrap',
                                    color:
                                      'warning.main',
                                  }}
                                >
                                  ₹
                                  {pendingAmt.toLocaleString(
                                    'en-IN',
                                    {
                                      minimumFractionDigits: 2,
                                    }
                                  )}
                                </Typography>
                              )}
                            </TableCell>


                            {/* INSTALLMENTS */}

                            <TableCell
                              sx={{
                                py: 1.2,
                                fontSize:
                                  '0.82rem',
                                textAlign:
                                  'center',
                              }}
                            >
                              {installmentCount !==
                              '—' ? (
                                <Chip
                                  label={`${installmentCount} inst.`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize:
                                      '0.7rem',
                                    height: 20,
                                    borderRadius:
                                      '4px',
                                    fontWeight: 600,
                                  }}
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>


                            {/* ACTION */}

                            <TableCell
                              align="center"
                              sx={{
                                py: 1.2,
                              }}
                            >
                              <Tooltip title="Edit Record">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleEdit(
                                      record
                                    )
                                  }
                                  sx={{
                                    color:
                                      'primary.main',

                                    '&:hover':
                                      {
                                        bgcolor:
                                          'primary.50',
                                      },
                                  }}
                                >
                                  <EditIcon
                                    sx={{
                                      fontSize: 16,
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    )
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>


        {!loadingRecords &&
          feeRecords.length >
            0 && (
            <TablePagination
              rowsPerPageOptions={[
                5,
                10,
                25,
                50,
              ]}
              component="div"
              count={
                feeRecords.length
              }
              rowsPerPage={
                rowsPerPage
              }
              page={page}
              onPageChange={(
                _,
                newPage
              ) =>
                setPage(
                  newPage
                )
              }
              onRowsPerPageChange={(
                e
              ) => {
                setRowsPerPage(
                  parseInt(
                    e.target
                      .value,
                    10
                  )
                );

                setPage(0);
              }}
              sx={{
                borderTop:
                  `1px solid ${theme.palette.divider}`,
              }}
            />
          )}
      </Paper>


      {/* ======================================================
          CANDIDATE SEARCH MODAL
      ====================================================== */}

      <Modal
        open={
          showCandidateModal
        }
        onClose={() =>
          setShowCandidateModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0,0,0,0.6)',
          },
        }}
      >
        <Box
          sx={{
            ...modalBox(1100),

            width: {
              xs: '95%',
              sm: '90%',
              md: 1100,
            },

            maxHeight:
              '88vh',

            display:
              'flex',

            flexDirection:
              'column',
          }}
        >
          <Box
            sx={
              modalHeader
            }
          >
            <Box
              sx={{
                display:
                  'flex',
                alignItems:
                  'center',
                gap: 1.5,
              }}
            >
              <PersonSearchIcon
                sx={{
                  color:
                    '#fff',
                }}
              />

              <Typography
                variant="h6"
                sx={{
                  color:
                    '#fff',
                  fontWeight: 700,
                }}
              >
                Select Candidate (No Fee Generated Yet)
              </Typography>
            </Box>

            <IconButton
              onClick={() =>
                setShowCandidateModal(
                  false
                )
              }
              sx={{
                color:
                  '#fff',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>


          {/* FINANCIAL YEAR */}

          <Box
            sx={{
              px: 2,
              py: 1.5,

              bgcolor:
                theme.palette
                  .mode ===
                'dark'
                  ? 'rgba(255,255,255,0.04)'
                  : 'rgba(25,118,210,0.04)',

              borderBottom:
                `1px solid ${theme.palette.divider}`,

              display:
                'flex',

              alignItems:
                'center',

              flexWrap:
                'wrap',

              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display:
                  'flex',
                alignItems:
                  'center',
                gap: 0.8,
                mr: 0.5,
              }}
            >
              <FilterListIcon
                sx={{
                  fontSize: 16,
                  color:
                    'primary.main',
                }}
              />

              <Typography
                sx={{
                  fontSize:
                    '0.78rem',
                  fontWeight: 700,
                  color:
                    'primary.main',
                  textTransform:
                    'uppercase',
                  letterSpacing:
                    0.8,
                }}
              >
                Financial Year Filter
              </Typography>
            </Box>


            <Box
              sx={{
                display:
                  'flex',
                alignItems:
                  'center',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize:
                    '0.78rem',
                  fontWeight: 500,
                  color:
                    'text.disabled',
                  whiteSpace:
                    'nowrap',
                }}
              >
                From Year
              </Typography>

              <TextField
                size="small"
                type="number"
                value={
                  filterFromYear
                }
                onChange={(e) =>
                  setFilterFromYear(
                    e.target.value
                  )
                }
                inputProps={{
                  min: 2020,
                  max:
                    currentYear +
                    5,

                  style: {
                    padding:
                      '5px 8px',
                    fontSize:
                      '0.85rem',
                    width: 72,
                    textAlign:
                      'center',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root':
                    {
                      borderRadius:
                        '6px',
                      height: 32,

                      '&:hover fieldset':
                        {
                          borderColor:
                            'primary.main',
                        },

                      '&.Mui-focused':
                        {
                          boxShadow:
                            '0 0 0 2px rgba(25,118,210,0.15)',
                        },
                    },
                }}
              />
            </Box>


            <Box
              sx={{
                display:
                  'flex',
                alignItems:
                  'center',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize:
                    '0.78rem',
                  fontWeight: 500,
                  color:
                    'text.disabled',
                  whiteSpace:
                    'nowrap',
                }}
              >
                To Year
              </Typography>

              <TextField
                size="small"
                type="number"
                value={
                  filterToYear
                }
                onChange={(e) =>
                  setFilterToYear(
                    e.target.value
                  )
                }
                inputProps={{
                  min: 2020,
                  max:
                    currentYear +
                    5,

                  style: {
                    padding:
                      '5px 8px',
                    fontSize:
                      '0.85rem',
                    width: 72,
                    textAlign:
                      'center',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root':
                    {
                      borderRadius:
                        '6px',
                      height: 32,

                      '&:hover fieldset':
                        {
                          borderColor:
                            'primary.main',
                        },

                      '&.Mui-focused':
                        {
                          boxShadow:
                            '0 0 0 2px rgba(25,118,210,0.15)',
                        },
                    },
                }}
              />
            </Box>


            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={
                handleApplyFilter
              }
              disabled={
                loadingCandidates
              }
              startIcon={
                loadingCandidates ? (
                  <CircularProgress
                    size={12}
                    color="inherit"
                  />
                ) : (
                  <FilterListIcon
                    sx={{
                      fontSize: 14,
                    }}
                  />
                )
              }
              sx={{
                borderRadius:
                  '6px',
                textTransform:
                  'none',
                fontWeight: 600,
                fontSize:
                  '0.78rem',
                px: 2,
                height: 32,
              }}
            >
              {loadingCandidates
                ? 'Loading…'
                : 'Apply'}
            </Button>


            {isFilterApplied && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={
                  handleClearFilter
                }
                disabled={
                  loadingCandidates
                }
                startIcon={
                  <CloseIcon
                    sx={{
                      fontSize: 14,
                    }}
                  />
                }
                sx={{
                  borderRadius:
                    '6px',
                  textTransform:
                    'none',
                  fontWeight: 600,
                  fontSize:
                    '0.78rem',
                  px: 2,
                  height: 32,
                  borderColor:
                    'divider',
                }}
              >
                Clear
              </Button>
            )}


            {isFilterApplied && (
              <Chip
                label={`FY ${filterFromYear}–${filterToYear}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  height: 24,
                  fontSize:
                    '0.72rem',
                  fontWeight: 700,
                  borderRadius:
                    '4px',
                }}
              />
            )}


            <Typography
              sx={{
                ml: 'auto',
                fontSize:
                  '0.72rem',
                color:
                  'text.disabled',
                fontStyle:
                  'italic',
              }}
            >
              {loadingCandidates
                ? 'Fetching…'
                : `${candidates.length} candidate(s) loaded`}
            </Typography>
          </Box>


          {/* SEARCH */}

          <Box
            sx={{
              p: 2,
              borderBottom:
                `1px solid ${theme.palette.divider}`,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, admission code, or course…"
              value={
                candidateSearchTerm
              }
              onChange={(e) =>
                setCandidateSearchTerm(
                  e.target.value
                )
              }
              autoFocus
              InputProps={{
                startAdornment:
                  (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          color:
                            'text.disabled',
                        }}
                      />
                    </InputAdornment>
                  ),

                endAdornment:
                  candidateSearchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setCandidateSearchTerm(
                            ''
                          )
                        }
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
              }}
              sx={inputSx}
            />
          </Box>


          {/* CANDIDATES */}

          <Box
            sx={{
              flex: 1,
              overflowY:
                'auto',
              p: 2,
              overflowX:
                'hidden',
            }}
          >
            <TableContainer
              component={
                Paper
              }
              variant="outlined"
              sx={{
                borderRadius:
                  1.5,
              }}
            >
              <Table
                stickyHeader
                size="small"
              >
                <TableHead>
                  <TableRow>
                    {[
                      'S.No',
                      'Candidate Name',
                      'Admission Code',
                      'Course Name & Code',
                      'Total Course Fee',
                      'Batch',
                      'Action',
                    ].map(
                      (
                        heading,
                        index
                      ) => (
                        <TableCell
                          key={
                            heading
                          }
                          align={
                            index ===
                            6
                              ? 'center'
                              : 'left'
                          }
                          sx={{
                            fontWeight: 700,
                            bgcolor:
                              theme
                                .palette
                                .background
                                .default,
                            fontSize:
                              '0.72rem',
                            textTransform:
                              'uppercase',
                            letterSpacing:
                              0.5,
                            color:
                              'primary.main',
                          }}
                        >
                          {
                            heading
                          }
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>


                <TableBody>
                  {loadingCandidates ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{
                          py: 5,
                        }}
                      >
                        <CircularProgress
                          size={24}
                        />

                        <Typography
                          variant="body2"
                          color="text.disabled"
                          sx={{
                            mt: 1,
                          }}
                        >
                          Loading candidates…
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (() => {
                      const term =
                        candidateSearchTerm
                          .toLowerCase()
                          .trim();

                      const filtered =
                        candidates.filter(
                          (
                            candidate
                          ) => {
                            if (
                              !term
                            ) {
                              return true;
                            }

                            return (
                              (
                                candidate.candidate_name ||
                                ''
                              )
                                .toLowerCase()
                                .includes(
                                  term
                                ) ||

                              (
                                candidate.admission_code ||
                                candidate.admission_no ||
                                ''
                              )
                                .toLowerCase()
                                .includes(
                                  term
                                ) ||

                              candidate.courses?.some(
                                (
                                  course
                                ) =>
                                  course.course_name
                                    ?.toLowerCase()
                                    .includes(
                                      term
                                    ) ||

                                  course.course_code
                                    ?.toLowerCase()
                                    .includes(
                                      term
                                    )
                              )
                            );
                          }
                        );


                      if (
                        candidates.length ===
                        0
                      ) {
                        return (
                          <TableRow>
                            <TableCell
                              colSpan={
                                7
                              }
                              align="center"
                              sx={{
                                py: 5,
                              }}
                            >
                              <PersonSearchIcon
                                sx={{
                                  fontSize: 36,
                                  color:
                                    'text.disabled',
                                  mb: 1,
                                  display:
                                    'block',
                                  mx: 'auto',
                                }}
                              />

                              <Typography
                                color="text.disabled"
                                variant="body2"
                              >
                                {isFilterApplied
                                  ? `No candidates found for FY ${filterFromYear}–${filterToYear}`
                                  : 'No candidates available'}
                              </Typography>

                              {isFilterApplied && (
                                <Button
                                  size="small"
                                  sx={{
                                    mt: 1,
                                    textTransform:
                                      'none',
                                    fontSize:
                                      '0.78rem',
                                  }}
                                  onClick={
                                    handleClearFilter
                                  }
                                >
                                  Clear filter to show all
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      }


                      if (
                        filtered.length ===
                        0
                      ) {
                        return (
                          <TableRow>
                            <TableCell
                              colSpan={
                                7
                              }
                              align="center"
                              sx={{
                                py: 5,
                              }}
                            >
                              <Typography
                                color="text.disabled"
                                variant="body2"
                              >
                                No candidates match "
                                {
                                  candidateSearchTerm
                                }
                                "
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      }


                      return filtered.map(
                        (
                          candidate,
                          index
                        ) => (
                          <TableRow
                            key={
                              candidate.id
                            }
                            hover
                            sx={{
                              cursor:
                                'pointer',

                              '&:hover':
                                {
                                  bgcolor:
                                    theme
                                      .palette
                                      .action
                                      .hover,
                                },

                              '&:last-child td':
                                {
                                  border: 0,
                                },
                            }}
                            onClick={() =>
                              handleCandidateSelect(
                                candidate
                              )
                            }
                          >
                            <TableCell>
                              {index + 1}
                            </TableCell>
                            {/* NAME */}

                            <TableCell
                              sx={{
                                py: 1.2,
                              }}
                            >
                              <Box
                                sx={{
                                  display:
                                    'flex',
                                  alignItems:
                                    'center',
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    bgcolor:
                                      'primary.main',
                                    fontSize:
                                      '0.72rem',
                                    fontWeight: 700,
                                  }}
                                >
                                  {(
                                    candidate.candidate_name ||
                                    'C'
                                  )[0].toUpperCase()}
                                </Avatar>

                                <Typography
                                  sx={{
                                    fontSize:
                                      '0.875rem',
                                    fontWeight: 500,
                                  }}
                                >
                                  {
                                    candidate.candidate_name ||
                                    'N/A'
                                  }
                                </Typography>
                              </Box>
                            </TableCell>


                            {/* ADMISSION */}

                            <TableCell
                              sx={{
                                fontFamily:
                                  'monospace',
                                fontSize:
                                  '0.82rem',
                              }}
                            >
                              {
                                candidate.admission_code ||
                                candidate.admission_no ||
                                '—'
                              }
                            </TableCell>


                            {/* COURSES */}

                            <TableCell
                              sx={{
                                py: 1.2,
                              }}
                            >
                              {candidate
                                .courses
                                ?.length >
                              0 ? (
                                candidate.courses.map(
                                  (
                                    course,
                                    index
                                  ) => (
                                    <Typography
                                      key={
                                        index
                                      }
                                      variant="body2"
                                      sx={{
                                        fontSize:
                                          '0.82rem',
                                      }}
                                    >
                                      {
                                        course.course_name
                                      }{' '}
                                      (
                                      {
                                        course.course_code
                                      }
                                      ) — ₹
                                      {
                                        course.total_course_fee
                                      }
                                    </Typography>
                                  )
                                )
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  No courses assigned
                                </Typography>
                              )}
                            </TableCell>


                            {/* TOTAL COURSE FEE */}

                            <TableCell
                              sx={{
                                py: 1.2,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color:
                                    'success.main',
                                  fontSize:
                                    '0.88rem',
                                }}
                              >
                                ₹
                                {(
                                  candidate.total_course_fee ||
                                  0
                                ).toFixed(
                                  2
                                )}
                              </Typography>
                            </TableCell>


                            {/* BATCH */}

                            <TableCell
                              sx={{
                                py: 1.2,
                              }}
                            >
                              {candidate.courses?.some(
                                (
                                  course
                                ) =>
                                  course
                                    .batches
                                    ?.length >
                                  0
                              ) ? (
                                candidate.courses.map(
                                  (
                                    course,
                                    courseIndex
                                  ) =>
                                    course.batches?.length >
                                    0
                                      ? course.batches.map(
                                          (
                                            batch,
                                            batchIndex
                                          ) => (
                                            <Box
                                              key={`${courseIndex}-${batchIndex}`}
                                              sx={{
                                                mb:
                                                  batchIndex <
                                                  course
                                                    .batches
                                                    .length -
                                                    1
                                                    ? 0.5
                                                    : 0,
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  fontSize:
                                                    '0.82rem',
                                                  fontWeight: 500,
                                                  lineHeight: 1.3,
                                                }}
                                              >
                                                {
                                                  batch.batch_name
                                                }
                                              </Typography>

                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontSize:
                                                    '0.7rem',
                                                  color:
                                                    'text.disabled',
                                                  fontFamily:
                                                    'monospace',
                                                }}
                                              >
                                                {
                                                  batch.batch_code
                                                }

                                                {course.course_code
                                                  ? ` · ${course.course_code}`
                                                  : ''}
                                              </Typography>
                                            </Box>
                                          )
                                        )
                                      : null
                                )
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.disabled"
                                >
                                  No batches
                                </Typography>
                              )}
                            </TableCell>


                            {/* ACTION */}

                            <TableCell
                              align="center"
                              sx={{
                                py: 1.2,
                              }}
                            >
                              <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                sx={{
                                  borderRadius:
                                    '6px',
                                  textTransform:
                                    'none',
                                  fontWeight: 600,
                                  fontSize:
                                    '0.78rem',
                                  px: 2,
                                }}
                                onClick={(
                                  e
                                ) => {
                                  e.stopPropagation();

                                  handleCandidateSelect(
                                    candidate
                                  );
                                }}
                              >
                                Select
                              </Button>
                            </TableCell>
                          </TableRow>
                        )
                      );
                    })()
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>


          <Box
            sx={{
              p: 2,
              borderTop:
                `1px solid ${theme.palette.divider}`,
              textAlign:
                'right',
            }}
          >
            <Button
              variant="outlined"
              sx={{
                borderRadius:
                  '6px',
                textTransform:
                  'none',
              }}
              onClick={() =>
                setShowCandidateModal(
                  false
                )
              }
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>


      {/* ======================================================
          DELETE DIALOG
      ====================================================== */}

      <Dialog
        open={
          deleteDialogOpen
        }
        onClose={() =>
          setDeleteDialogOpen(
            false
          )
        }
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems:
              'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius:
                '50%',
              bgcolor:
                'error.50',
              display: 'flex',
              alignItems:
                'center',
              justifyContent:
                'center',
            }}
          >
            <DeleteIcon
              sx={{
                color:
                  'error.main',
                fontSize: 20,
              }}
            />
          </Box>

          Confirm Delete
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              color:
                theme.palette
                  .text.primary,
            }}
          >
            Are you sure you want to delete{' '}
            {
              selectedRecords.length
            }{' '}
            fee record(s)?
            This action
            cannot be
            undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            gap: 1,
          }}
        >
          <Button
            onClick={() =>
              setDeleteDialogOpen(
                false
              )
            }
            sx={{
              borderRadius:
                '6px',
              textTransform:
                'none',
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            onClick={
              handleDelete
            }
            color="error"
            variant="contained"
            disableElevation
            sx={{
              borderRadius:
                '6px',
              textTransform:
                'none',
              fontWeight: 600,
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>


      {/* ======================================================
          SUCCESS MODAL
      ====================================================== */}

      <Modal
        open={
          showSuccessModal
        }
        onClose={() =>
          setShowSuccessModal(
            false
          )
        }
      >
        <Box
          sx={{
            ...modalBox(360),
            p: 4,
            textAlign:
              'center',
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 54,
              color:
                'success.main',
              mb: 1.5,
            }}
          />

          <Typography
            variant="h6"
            fontWeight={700}
            gutterBottom
          >
            Fee Record{' '}
            {successAction}
          </Typography>

          <Typography
            color="text.disabled"
            sx={{
              mb: 3,
              fontSize:
                '0.9rem',
            }}
          >
            The fee record has
           been {successAction.toLowerCase()} successfully.
          </Typography>

          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              setShowSuccessModal(
                false
              );

              handleReset();
            }}
            sx={{
              borderRadius:
                '6px',
              minWidth: 120,
              textTransform:
                'none',
              fontWeight: 600,
            }}
          >
            Done
          </Button>
        </Box>
      </Modal>


      {/* ======================================================
          SNACKBAR
      ====================================================== */}

      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(
              false
            )
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: '100%',
          }}
        >
          {
            snackbarMessage
          }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FeeGeneration;